import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Alert, Box, Button, Checkbox, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, InputAdornment, Menu, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import SCurveChart from './SCurveChart';
import UKProjectMap from './UKProjectMap';
import { formatReportVersion, getReportVersionNaming, getReportVersionNoun } from './reportVersioning';
import useDeviceType from './useDeviceType';
import { computeSCurveBaseline } from './ProjectCostsSummaryTable';
import { AnalyticsMetricCard, AnalyticsPageHero, AnalyticsPanelSection, analyticsPanelSx } from './analyticsSurface';
import { API_BASE_URL as SHARED_API_BASE_URL } from './api';
import { readOfficeMapSettingsCache } from './officeMapSettingsCache';

const INTERNAL_NAVIGATION_MARKER_KEY = 'monsurv_internal_navigation_v1';

function markInternalNavigation() {
    try {
        sessionStorage.setItem(INTERNAL_NAVIGATION_MARKER_KEY, String(Date.now()));
    } catch {
        // ignore
    }
}

const OFFICE_MAP_SETTINGS_UPDATED_EVENT = 'monsurv:office-map-settings-updated';

// --- Risk Matrix API Integration ---
const RISK_API_BASE_URL = SHARED_API_BASE_URL;
// Backend supports /summary_risk_rating; keep requests single-path to avoid 404 spam.
const RISK_API_PATHS = ['/summary_risk_rating'];

// Shared API base URL (dashboard backend)
const API_BASE_URL = RISK_API_BASE_URL;

function fetchJson(url, options) {
    const nextOptions = { ...(options || {}) };
    const existingHeaders = nextOptions.headers;
    let headers = {};
    try {
        if (existingHeaders) {
            if (typeof Headers !== 'undefined' && existingHeaders instanceof Headers) {
                headers = Object.fromEntries(existingHeaders.entries());
            } else {
                headers = { ...(existingHeaders || {}) };
            }
        }
    } catch {
        headers = { ...(existingHeaders || {}) };
    }

    try {
        const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
        const hasAuth = Boolean(headers.Authorization || headers.authorization);
        if (token && !hasAuth) headers.Authorization = `Bearer ${token}`;
    } catch {
        // ignore
    }

    nextOptions.headers = headers;

    return fetch(url, nextOptions).then(async (res) => {
        if (res.status === 204) return null;

        let json;
        try {
            json = await res.json();
        } catch (e) {
            json = null;
        }

        if (!res.ok) {
            const msg = (json && (json.error || json.message)) ? (json.error || json.message) : `HTTP ${res.status}`;
            const err = new Error(msg);
            err.status = res.status;
            err.body = json;
            throw err;
        }

        return json;
    });
}

function clampNumber(value, min, max, fallback) {
    const n = Number(value);
    if (!Number.isFinite(n)) return fallback;
    return Math.max(min, Math.min(max, n));
}

function tryGetDashboardRoleFromToken(token) {
    try {
        const parts = String(token || '').split('.');
        if (parts.length < 2) return null;
        const raw = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = raw.padEnd(Math.ceil(raw.length / 4) * 4, '=');
        const json = atob(padded);
        const payload = JSON.parse(json);
        const role = String(payload?.role || '').trim().toLowerCase();
        return role || null;
    } catch {
        return null;
    }
}

function healthIndexColor(score) {
    const s = clampNumber(score, 0, 100, 50);
    // 0 -> red (0deg), 50 -> yellow (60deg), 100 -> green (120deg)
    const hue = (s / 100) * 120;
    return `hsl(${hue}, 85%, 38%)`;
}

function ragColor(rag) {
    const r = String(rag || '').trim().toLowerCase();
    if (r === 'high') return '#dc2626';
    if (r === 'medium') return '#d97706';
    if (r === 'low') return '#16a34a';
    if (r === 'red') return '#dc2626';
    if (r === 'amber' || r === 'yellow') return '#d97706';
    if (r === 'green') return '#16a34a';
    return '#64748b';
}

function normalizeRiskRating(value) {
    const v = String(value || '').trim().toLowerCase();
    if (!v) return '';
    // Canonical values:
    if (v === 'low' || v === 'l') return 'Low';
    if (v === 'medium' || v === 'med' || v === 'm') return 'Medium';
    if (v === 'high' || v === 'h') return 'High';
    // Back-compat legacy RAG:
    if (v === 'green' || v === 'g') return 'Low';
    if (v === 'amber' || v === 'yellow' || v === 'a' || v === 'y') return 'Medium';
    if (v === 'red' || v === 'r') return 'High';
    // Leave unknown values as-is.
    return String(value).trim();
}

function svgPlaceholderDataUri(label) {
    const safe = String(label || 'Image').trim() || 'Image';
    const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
  <rect width="400" height="300" fill="#f1f5f9"/>
  <rect x="18" y="18" width="364" height="264" rx="16" fill="#ffffff" stroke="rgba(15,23,42,0.10)"/>
  <text x="200" y="150" text-anchor="middle" dominant-baseline="middle" font-family="Inter,Segoe UI,Arial" font-size="18" font-weight="800" fill="#64748b">${safe}</text>
</svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function parseSnapshotSummaryText(rawText) {
    const text = String(rawText || '').replace(/\r\n/g, '\n');
    const lines = text.split('\n');

    const out = {
        project_description: '',
        site_progress: '',
        overview: '',
        programme: '',
        costs: '',
        actions: '',
        risks: {}
    };

    const normHeading = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');

    const sectionForHeading = (heading) => {
        const h = normHeading(heading);
        if (h === 'project description' || h === 'project description:' || h === 'project description (intended development)' || h === 'project description (intended development):' || h === 'intended development' || h === 'intended development:') return 'project_description';
        if (h === 'site progress' || h === 'site progress:' || h === 'construction progress' || h === 'construction progress:' || h === 'progress' || h === 'progress:') return 'site_progress';
        if (h === 'project overview' || h === 'project overview:' || h === 'project overview (initial)') return 'overview';
        if (h === 'programme' || h === 'program') return 'programme';
        if (h === 'costs / drawdown' || h === 'costs/drawdown' || h === 'costs' || h === 'costs & drawdown' || h === 'drawdown' || h === 'costs / drawdown:') return 'costs';
        if (h === 'risks (rag)' || h === 'risks (rag):' || h === 'risks' || h === 'risk (rag)' || h === 'risk (rag):') return 'risks';
        if (h === 'key actions / next steps' || h === 'key actions/next steps' || h === 'next steps' || h === 'key actions' || h === 'key actions:' || h === 'key actions / next steps:') return 'actions';
        return '';
    };

    const normalizeRiskKey = (label) => {
        const l = normHeading(label)
            .replace(/[()]/g, '')
            .replace(/\s*\/\s*/g, ' / ');
        if (l === 'planning / statutory') return 'Planning / Statutory';
        if (l === 'outstanding information' || l === 'outstanding info') return 'Outstanding Information';
        if (l === 'building control') return 'Building Control';
        if (l === 'building warranty') return 'Building Warranty';
        if (l === 'procurement') return 'Procurement';
        if (l === 'contract') return 'Contract';
        if (l === 'insurances' || l === 'insurance') return 'Insurances';
        if (l === 'programme' || l === 'program') return 'Programme';
        if (l === 'costs') return 'Costs';
        if (l === 'site') return 'Site';
        return String(label || '').trim() || label;
    };

    let currentSection = '';
    let buf = [];
    let lastRiskKey = '';

    const flush = () => {
        const block = buf.join('\n').trim();
        if (!block) {
            buf = [];
            return;
        }
        if (currentSection === 'project_description') out.project_description = out.project_description ? `${out.project_description}\n${block}` : block;
        else if (currentSection === 'site_progress') out.site_progress = out.site_progress ? `${out.site_progress}\n${block}` : block;
        if (currentSection === 'overview') out.overview = out.overview ? `${out.overview}\n${block}` : block;
        else if (currentSection === 'programme') out.programme = out.programme ? `${out.programme}\n${block}` : block;
        else if (currentSection === 'costs') out.costs = out.costs ? `${out.costs}\n${block}` : block;
        else if (currentSection === 'actions') out.actions = out.actions ? `${out.actions}\n${block}` : block;
        buf = [];
    };

    for (const lineRaw of lines) {
        const line = String(lineRaw || '');
        const trimmed = line.trim();

        // Detect section headings (exact line match).
        const sec = sectionForHeading(trimmed);
        if (sec) {
            flush();
            currentSection = sec;
            lastRiskKey = '';
            continue;
        }

        if (currentSection === 'risks') {
            // Parse "Key: ..." risk lines, and allow wrapped continuation lines.
            // Accept empty values so lines like "Outstanding Information:" switch the active key.
            const m = trimmed.match(/^([^:]{2,60}):\s*(.*)$/);
            if (m) {
                const k = normalizeRiskKey(m[1]);
                const v = String(m[2] || '').trim();
                if (v) {
                    out.risks[k] = out.risks[k] ? `${out.risks[k]}\n${v}` : v;
                }
                lastRiskKey = k;
                continue;
            }

            if (lastRiskKey && trimmed) {
                out.risks[lastRiskKey] = out.risks[lastRiskKey]
                    ? `${out.risks[lastRiskKey]}\n${trimmed}`
                    : trimmed;
                continue;
            }

            continue;
        }

        // Default: collect lines for the current section.
        if (currentSection) {
            buf.push(trimmed);
        } else {
            // If no headings yet, treat leading text as overview.
            currentSection = 'overview';
            buf.push(trimmed);
        }
    }

    flush();

    // If the overview starts with a duplicated header-like line, keep as-is (user wants full text).
    return out;
}

function parseSnapshotRiskValue(value) {
    const raw = String(value || '').trim();
    if (!raw) return { rating: '', narrative: '' };

    const match = raw.match(/^(low|medium|high|green|amber|yellow|red)\s*[-:–—]?\s*(.*)$/i);
    if (!match) {
        return {
            rating: '',
            narrative: raw
        };
    }

    return {
        rating: normalizeRiskRating(match[1]),
        narrative: String(match[2] || '').trim()
    };
}

function getSnapshotPreviewLine(value) {
    const lines = String(value || '')
        .replace(/\r\n/g, '\n')
        .split('\n')
        .map((line) => String(line || '').trim())
        .filter(Boolean);

    if (!lines.length) return '';

    const first = lines[0].replace(/^[-*•]\s+/, '').trim();
    const sentence = first.match(/^(.{1,180}?[.!?])(?:\s|$)/);
    return sentence ? sentence[1].trim() : first;
}

function getSnapshotDisplayLines(value) {
    return String(value || '')
        .replace(/\r\n/g, '\n')
        .split('\n')
        .map((line) => String(line || '').trim())
        .filter(Boolean);
}

function hasLongSnapshotSection(value) {
    const lines = getSnapshotDisplayLines(value);
    if (lines.length > 4) return true;
    return lines.some((line) => line.length > 180);
}

function normalizeProjectOfficeMapOffice(raw, index) {
    const value = raw && typeof raw === 'object' ? raw : {};
    return {
        id: String(value.id || `office_${index + 1}`).trim() || `office_${index + 1}`,
        office_location: String(value.office_location || '').trim(),
        address: String(value.address || '').trim(),
        pin_color: String(value.pin_color || '#0f3b68').trim() || '#0f3b68'
    };
}

function normalizeProjectOfficeMapSettings(raw) {
    const value = raw && typeof raw === 'object' ? raw : {};
    const legacyOffices = (value.office_location || value.address || value.pin_color)
        ? [value]
        : [];
    const offices = (Array.isArray(value.offices) ? value.offices : legacyOffices)
        .map((office, index) => normalizeProjectOfficeMapOffice(office, index))
        .filter((office) => office.office_location || office.address);
    const primaryOfficeId = offices.some((office) => office.id === value.primary_office_id)
        ? String(value.primary_office_id)
        : (offices[0]?.id || '');
    const primaryOffice = offices.find((office) => office.id === primaryOfficeId) || offices[0] || null;
    return {
        offices,
        primary_office_id: primaryOfficeId,
        office_location: String(primaryOffice?.office_location || '').trim(),
        address: String(primaryOffice?.address || '').trim(),
        pin_color: String(primaryOffice?.pin_color || '#0f3b68').trim() || '#0f3b68',
        brand_upload_url: String(value.brand_upload_url || '').trim()
    };
}

function choosePreferredProjectOfficeMapSettings(serverSettings, cachedSettings) {
    const server = normalizeProjectOfficeMapSettings(serverSettings || null);
    const cached = cachedSettings ? normalizeProjectOfficeMapSettings(cachedSettings) : null;
    if (!cached) return server;
    const serverCount = Array.isArray(server?.offices) ? server.offices.length : 0;
    const cachedCount = Array.isArray(cached?.offices) ? cached.offices.length : 0;
    if (cachedCount > serverCount) return { ...server, ...cached, brand_upload_url: cached.brand_upload_url || server.brand_upload_url || '' };
    return server;
}

const HEALTH_INDEX_CACHE_KEY = 'monsurv_project_health_index_cache_v2';
const PHI_CONFIG_KEY = 'monsurv_phi_config';

// PHI is intentionally independent of report readiness.
const PHI_WEIGHT_KEYS = ['risk_matrix', 'report_recency', 'outstanding_info', 'cost_suitability'];

function loadPhiConfig() {
    const fallback = {
        enabled: true,
        use_ai: true,
        ttl_minutes: 30,
        report_instances: {
            initial: {
                weights: {
                    risk_matrix: 100,
                    report_recency: 100,
                    outstanding_info: 100,
                    cost_suitability: 100
                }
            },
            interim: {
                weights: {
                    risk_matrix: 100,
                    report_recency: 100,
                    outstanding_info: 100,
                    cost_suitability: 100
                }
            }
        },
        factors: {
            risk_matrix: true,
            report_recency: true,
            outstanding_info: true,
            cost_suitability: true
        }
    };

    try {
        const raw = localStorage.getItem(PHI_CONFIG_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (!parsed || typeof parsed !== 'object') return fallback;
        const factors = parsed.factors && typeof parsed.factors === 'object' ? parsed.factors : {};

        const parsedInstances = parsed.report_instances && typeof parsed.report_instances === 'object' ? parsed.report_instances : {};
        const parsedInitial = parsedInstances.initial && typeof parsedInstances.initial === 'object' ? parsedInstances.initial : {};
        const parsedInterim = parsedInstances.interim && typeof parsedInstances.interim === 'object' ? parsedInstances.interim : {};

        const mergeWeights = (defaultsWeights, rawNode) => {
            const rawWeights = rawNode && typeof rawNode.weights === 'object' ? rawNode.weights : {};
            const legacy = clampInt(rawNode?.weighting, 0, 200, null);
            const legacyFill = legacy == null ? {} : {
                risk_matrix: legacy,
                report_recency: legacy
            };
            return { ...defaultsWeights, ...legacyFill, ...rawWeights };
        };

        return {
            ...fallback,
            ...parsed,
            report_instances: {
                ...fallback.report_instances,
                ...parsedInstances,
                initial: {
                    ...fallback.report_instances.initial,
                    ...parsedInitial,
                    weights: mergeWeights(fallback.report_instances.initial.weights, parsedInitial)
                },
                interim: {
                    ...fallback.report_instances.interim,
                    ...parsedInterim,
                    weights: mergeWeights(fallback.report_instances.interim.weights, parsedInterim)
                }
            },
            factors: { ...fallback.factors, ...factors }
        };
    } catch {
        return fallback;
    }
}

function clampInt(value, min, max, fallback) {
    const n = Number(value);
    if (!Number.isFinite(n)) return fallback;
    return Math.max(min, Math.min(max, Math.round(n)));
}

function getPhiWeightsForInstance(cfg, reportInstance) {
    const c = cfg && typeof cfg === 'object' ? cfg : {};
    const instances = c.report_instances && typeof c.report_instances === 'object' ? c.report_instances : {};
    const isInterim = String(reportInstance || '').toLowerCase().startsWith('interim');
    const node = isInterim ? instances.interim : instances.initial;
    const weights = node && typeof node.weights === 'object' ? node.weights : {};

    // Back-compat: old schema stored a single `weighting`.
    const legacyWeighting = clampInt(node?.weighting, 0, 200, null);

    const out = {};
    for (const k of PHI_WEIGHT_KEYS) {
        const fromWeights = clampInt(weights?.[k], 0, 200, null);
        out[k] = fromWeights != null ? fromWeights : (legacyWeighting != null ? legacyWeighting : 100);
    }
    return out;
}

function weightsToQueryValue(weights) {
    return PHI_WEIGHT_KEYS
        .map((k) => `${k}:${clampInt(weights?.[k], 0, 200, 100)}`)
        .join(',');
}

function weightsToSig(weights) {
    // compact cache key signature (stable order)
    return PHI_WEIGHT_KEYS
        .map((k) => `${k[0]}${clampInt(weights?.[k], 0, 200, 100)}`)
        .join('');
}

function phiFactorsToQuery(cfg) {
    const c = cfg && typeof cfg === 'object' ? cfg : {};
    const f = c.factors && typeof c.factors === 'object' ? c.factors : {};
    const enabled = [];
    if (f.risk_matrix) enabled.push('risk_matrix');
    if (f.report_recency) enabled.push('report_recency');
    if (f.outstanding_info) enabled.push('outstanding_info');
    if (f.cost_suitability) enabled.push('cost_suitability');
    return enabled.join(',');
}

const RISK_CRITERIA_LIST = ['Site', 'Planning', 'Programme', 'Costs', 'Contract', 'Insurances', 'Outstanding Information'];
const RISK_FIELD_MAP = {
    Site: 'site',
    Planning: 'planning',
    Programme: 'programme',
    Costs: 'costs',
    Contract: 'contract',
    Insurances: 'insurances',
    'Outstanding Information': 'outstanding_info'
};

function normalizeRiskMatrix(apiData) {
    const filled = {};
    RISK_CRITERIA_LIST.forEach((c) => {
        filled[c] = { rating: '', comment: '' };
    });
    filled['Building Control'] = { rating: '', comment: '' };
    filled['Building Warranty'] = { rating: '', comment: '' };
    filled.Procurement = { rating: '', comment: '' };

    if (!apiData) return filled;

    if (apiData.data && typeof apiData.data === 'object') {
        const fromLegacyData = normalizeRiskMatrix(apiData.data);
        Object.assign(filled, fromLegacyData);
    }

    RISK_CRITERIA_LIST.forEach((c) => {
        const v = apiData[c];
        if (v && typeof v === 'object') {
            if (typeof v.rating === 'string') filled[c].rating = normalizeRiskRating(v.rating);
            if (typeof v.comment === 'string') filled[c].comment = v.comment;
        }
    });

    RISK_CRITERIA_LIST.forEach((c) => {
        const prefix = RISK_FIELD_MAP[c];
        if (!prefix) return;
        const rag = apiData[`${prefix}_rag`];
        const comment = apiData[`${prefix}_comment`];
        if (typeof rag === 'string') filled[c].rating = normalizeRiskRating(rag);
        if (typeof comment === 'string') filled[c].comment = comment;
    });

    // Extended questionnaire fields available in summary_risk_rating.
    if (typeof apiData.building_control_rag === 'string') {
        filled['Building Control'].rating = normalizeRiskRating(apiData.building_control_rag);
    }
    if (typeof apiData.building_control_comment === 'string') {
        filled['Building Control'].comment = apiData.building_control_comment;
    }

    if (typeof apiData.building_warranty_rag === 'string') {
        filled['Building Warranty'].rating = normalizeRiskRating(apiData.building_warranty_rag);
    }
    if (typeof apiData.building_warranty_comment === 'string') {
        filled['Building Warranty'].comment = apiData.building_warranty_comment;
    }

    if (typeof apiData.procurement_rag === 'string') {
        filled.Procurement.rating = normalizeRiskRating(apiData.procurement_rag);
    }
    if (typeof apiData.procurement_comment === 'string') {
        filled.Procurement.comment = apiData.procurement_comment;
    }

    return filled;
}

function loadHealthIndexCache() {
    try {
        const raw = localStorage.getItem(HEALTH_INDEX_CACHE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

function saveHealthIndexCache(cache) {
    try {
        localStorage.setItem(HEALTH_INDEX_CACHE_KEY, JSON.stringify(cache || {}));
    } catch {
        // ignore
    }
}

async function tryRiskPaths(paths, makeUrl, options) {
    let lastErr;
    for (const path of paths) {
        try {
            return await fetchJson(makeUrl(path), options);
        } catch (e) {
            if (e && e.status && e.status !== 404) throw e;
            lastErr = e;
        }
    }
    throw lastErr || new Error('No API path succeeded');
}

async function fetchRiskMatrix(projectReference) {
    return tryRiskPaths(
        RISK_API_PATHS,
        // allowMissing=1 makes the backend return 204 instead of 404 when not yet set.
        (path) => `${API_BASE_URL}${path}/${encodeURIComponent(projectReference)}?allowMissing=1`,
        { method: 'GET' }
    );
}

async function fetchQuestionnaireOutstandingInfo(projectReference) {
    return fetchJson(
        `${API_BASE_URL}/project/${encodeURIComponent(projectReference)}/information-outstanding`,
        { method: 'GET' }
    );
}

async function fetchQuestionnaireCustomRisks(projectReference) {
    return fetchJson(
        `${API_BASE_URL}/project/${encodeURIComponent(projectReference)}/custom-risks`,
        { method: 'GET' }
    );
}

async function saveRiskMatrix(payload) {
    return tryRiskPaths(
        RISK_API_PATHS,
        (path) => `${API_BASE_URL}${path}`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        }
    );
}

function getProjectLogKey(projectId) {
    return `monsurv_report_log_${projectId}`;
}

function loadReportLog(projectId) {
    if (!projectId) return [];
    try {
        const raw = localStorage.getItem(getProjectLogKey(projectId));
        return raw ? JSON.parse(raw) : [];
    } catch (e) {
        return [];
    }
}

function saveReportLog(projectId, log) {
    if (!projectId) return;
    try {
        localStorage.setItem(getProjectLogKey(projectId), JSON.stringify(log));
    } catch (e) { }
}

function clearReportLogCache(projectId) {
    if (!projectId) return;
    try {
        localStorage.removeItem(getProjectLogKey(projectId));
    } catch (e) { }
}

function formatDDMMYY(dateLike) {
    if (!dateLike) return '';
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}/${mm}/${yy}`;
}

function formatDDMMYYYY(dateLike) {
    if (!dateLike) return '';
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = String(d.getFullYear());
    return `${dd}/${mm}/${yyyy}`;
}

function isoToYearMonth(isoLike) {
    if (!isoLike) return null;
    const d = new Date(isoLike);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
}

function normalizeReportLogItems(items) {
    const arr = Array.isArray(items) ? items : [];
    return arr.map((r) => {
        const downloadUrl = r?.download_url || (r?.file_path ? `/uploads/${String(r.file_path).replace(/^\/+/, '')}` : null);
        const downloadHref = downloadUrl ? `${API_BASE_URL}${downloadUrl}` : null;
        const issuePdfUrl = r?.issue_pdf_download_url || (r?.issue_pdf_file_path ? `/uploads/${String(r.issue_pdf_file_path).replace(/^\/+/, '')}` : null);
        const issuePdfHref = issuePdfUrl ? `${API_BASE_URL}${issuePdfUrl}` : null;
        return {
            id: r?.id,
            type: r?.report_type || r?.type || '—',
            revision: r?.revision ?? r?.rev ?? '—',
            date: r?.generated_at ? formatDDMMYYYY(r.generated_at) : (r?.date || ''),
            qaChecked: Boolean(r?.qa_checked ?? r?.qaChecked),
            qaName: r?.qa_name || r?.qaName || '',
            issueChecked: Boolean(r?.issue_checked ?? r?.issueChecked),
            issueName: r?.issued_by || r?.issueName || '',
            issueDate: r?.issue_date ? formatDDMMYY(r.issue_date) : (r?.issueDate || ''),
            fileName: r?.file_name || r?.fileName || '',
            downloadHref,
            issuePdfHref
        };
    });
}

function parseInterimReportNumber(reportLike) {
    const candidates = [
        String(reportLike?.report_instance || '').trim(),
        String(reportLike?.report_type || '').trim(),
        String(reportLike?.file_name || '').trim(),
        String(reportLike?.download_url || '').trim()
    ].filter(Boolean);

    for (const candidate of candidates) {
        const lower = candidate.toLowerCase();

        let match = lower.match(/^interim:(\d+)$/);
        if (match) return match[1];

        match = lower.match(/interim(?:\s+report)?[\s:_-]+(\d+)\b/);
        if (match) return match[1];

        match = lower.match(/interim_report(?:_valuation)?_(\d+)\b/);
        if (match) return match[1];

        match = lower.match(/\bir[\s:_-]?(\d+)\b/);
        if (match) return match[1];
    }

    return '';
}

function getProjectReportBadges({ reportState, initialConfirmedLive, inProgressReportType, inProgressInterimNumber }) {
    if (!initialConfirmedLive) return ['Initial Report'];

    const inProgressType = String(inProgressReportType || '').trim().toLowerCase();
    const inProgressNo = Number(inProgressInterimNumber || 0);
    if (inProgressType === 'interim' && Number.isFinite(inProgressNo) && inProgressNo > 0) {
        return [`Interim Report ${Math.trunc(inProgressNo)}`];
    }

    const issuedInterimNumber = Number(parseInterimReportNumber(reportState?.interim) || 0);
    const nextExpectedInterimNumber = Math.max(1, issuedInterimNumber + 1);

    return [`Interim Report ${nextExpectedInterimNumber}`];
}

// Fetch key_project_data from backend

const summaryFields = [
    { key: 'name', label: 'Project Name' },
    { key: 'reference', label: 'Project Reference No.' },
    { key: 'siteAddress', label: 'Site Address' },
    { key: 'borrower', label: 'Borrower Name' },
    { key: 'borrowerCompany', label: 'Borrower Company' },
    { key: 'borrowerEmail', label: 'Borrower Email' },
    { key: 'funder', label: 'Funder Name' },
    { key: 'leadSurveyor', label: 'Lead Surveyor' },
    { key: 'facility', label: 'Project Facility' },
    { key: 'duration', label: 'Project Duration (Months)' },
    { key: 'worksStartDate', label: 'Works Start Date' },
    { key: 'projectedCompletion', label: 'Projected Completion Date', calculated: true }
];

function parseMonthInput(value) {
    const s = String(value || '').trim();
    const m = s.match(/^(\d{4})-(\d{2})$/);
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null;
    return { year, month };
}

function addMonthsToMonthYear(monthInput, monthsToAdd) {
    const parsed = parseMonthInput(monthInput);
    const n = Number(monthsToAdd);
    if (!parsed || !Number.isFinite(n) || n <= 0) return '—';

    const totalMonths = (parsed.year * 12 + (parsed.month - 1)) + Math.trunc(n);
    const year = Math.floor(totalMonths / 12);
    const month = (totalMonths % 12) + 1;
    return `${String(month).padStart(2, '0')}/${String(year)}`;
}

function toMoneyNumber(value) {
    const n = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
}

function getDefaultSectionRows() {
    return [{ item: '', budget: '', costToDate: '' }];
}

function getDefaultPreviousDrawdownRows(interimReportNumber) {
    const n = Number(interimReportNumber);
    const count = Number.isFinite(n) && n > 0 ? Math.trunc(n) : 1;
    return Array.from({ length: count }).map((_, idx) => ({ item: `Drawdown ${idx + 1}`, amount: '' }));
}

function normalizeSectionRows(rows) {
    if (!Array.isArray(rows) || !rows.length) return getDefaultSectionRows();
    const normalized = rows.map((row) => ({
        item: String(row?.item ?? '').trim(),
        budget: String(row?.budget ?? row?.budgetItem ?? row?.amount ?? '').trim(),
        costToDate: String(row?.costToDate ?? '').trim()
    }));
    return normalized.length ? normalized : getDefaultSectionRows();
}

function classifyLegacyBudgetRow(itemLabel) {
    const label = String(itemLabel || '').trim().toLowerCase();
    if (!label) return 'constructionBudgetRows';
    if (label.includes('contingency')) return 'contingencyBudgetRows';
    if (label.includes('professional')) return 'professionalFeeBudgetRows';
    if (label.includes('additional') || label.includes('other')) return 'additionalCostBudgetRows';
    return 'constructionBudgetRows';
}

function normalizeDevelopmentCosts(raw, interimReportNumber) {
    const value = raw && typeof raw === 'object' ? raw : {};

    const borrowerInputRelevantRaw = value.borrowerInputRelevant ?? value.borrower_input_relevant;
    const borrowerInputRelevant = borrowerInputRelevantRaw === true || String(borrowerInputRelevantRaw || '').trim().toLowerCase() === 'yes' || String(borrowerInputRelevantRaw || '').trim().toLowerCase() === 'true';
    const borrowerInputAmount = String(value.borrowerInputAmount ?? value.borrower_input_amount ?? '').trim();
    const contingencyBudgetTotal = String(value.contingencyBudgetTotal ?? value.contingency_budget_total ?? '').trim();

    const hasSectionRows =
        Array.isArray(value.constructionBudgetRows)
        || Array.isArray(value.contingencyBudgetRows)
        || Array.isArray(value.professionalFeeBudgetRows)
        || Array.isArray(value.additionalCostBudgetRows);

    const developmentCosts = {
        constructionBudgetRows: hasSectionRows ? normalizeSectionRows(value.constructionBudgetRows) : getDefaultSectionRows(),
        contingencyBudgetRows: hasSectionRows ? normalizeSectionRows(value.contingencyBudgetRows) : getDefaultSectionRows(),
        professionalFeeBudgetRows: hasSectionRows ? normalizeSectionRows(value.professionalFeeBudgetRows) : getDefaultSectionRows(),
        additionalCostBudgetRows: hasSectionRows ? normalizeSectionRows(value.additionalCostBudgetRows) : getDefaultSectionRows(),
        contingencyBudgetTotal,
        borrowerInputRelevant,
        borrowerInputAmount,
        previousDrawdownRows: Array.isArray(value.previousDrawdownRows) && value.previousDrawdownRows.length
            ? value.previousDrawdownRows.map((row, idx) => ({
                item: String(row?.item || `Drawdown ${idx + 1}`).trim() || `Drawdown ${idx + 1}`,
                amount: String(row?.amount ?? '').trim()
            }))
            : getDefaultPreviousDrawdownRows(interimReportNumber)
    };

    if (!hasSectionRows && Array.isArray(value.budgetRows) && value.budgetRows.length) {
        const sections = {
            constructionBudgetRows: [],
            contingencyBudgetRows: [],
            professionalFeeBudgetRows: [],
            additionalCostBudgetRows: []
        };

        value.budgetRows.forEach((row) => {
            const sectionKey = classifyLegacyBudgetRow(row?.item);
            sections[sectionKey].push({
                item: String(row?.item ?? '').trim(),
                budget: String(row?.amount ?? '').trim(),
                costToDate: ''
            });
        });

        developmentCosts.constructionBudgetRows = sections.constructionBudgetRows.length ? sections.constructionBudgetRows : getDefaultSectionRows();
        developmentCosts.contingencyBudgetRows = sections.contingencyBudgetRows.length ? sections.contingencyBudgetRows : getDefaultSectionRows();
        developmentCosts.professionalFeeBudgetRows = sections.professionalFeeBudgetRows.length ? sections.professionalFeeBudgetRows : getDefaultSectionRows();
        developmentCosts.additionalCostBudgetRows = sections.additionalCostBudgetRows.length ? sections.additionalCostBudgetRows : getDefaultSectionRows();

        if (!developmentCosts.contingencyBudgetTotal) {
            const contingencyLegacyTotal = sections.contingencyBudgetRows.reduce((sum, row) => sum + toMoneyNumber(row?.budget), 0);
            developmentCosts.contingencyBudgetTotal = contingencyLegacyTotal ? String(contingencyLegacyTotal) : '';
        }

        if (sections.contingencyBudgetRows.length) {
            developmentCosts.contingencyBudgetRows = sections.contingencyBudgetRows.map((row) => ({
                item: String(row?.item || '').trim(),
                budget: '',
                costToDate: String(row?.costToDate || '').trim()
            }));
        }
    }

    if ((!Array.isArray(value.previousDrawdownRows) || !value.previousDrawdownRows.length) && Array.isArray(value.drawdownRows) && value.drawdownRows.length) {
        developmentCosts.previousDrawdownRows = value.drawdownRows.map((row, idx) => ({
            item: String(row?.item || `Drawdown ${idx + 1}`).trim() || `Drawdown ${idx + 1}`,
            amount: String(row?.amount ?? '').trim()
        }));
    }

    return developmentCosts;
}

function sumSectionBudget(rows) {
    return (Array.isArray(rows) ? rows : []).reduce((sum, row) => sum + toMoneyNumber(row?.budget), 0);
}

function sumSectionCostToDate(rows) {
    return (Array.isArray(rows) ? rows : []).reduce((sum, row) => sum + toMoneyNumber(row?.costToDate), 0);
}

function formatPounds(value) {
    return Number(value || 0).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
}

function normalizeProjectSetupRecord(raw) {
    const row = raw && typeof raw === 'object' ? raw : {};
    const programmeRaw = row.programme && typeof row.programme === 'object' ? row.programme : {};
    const developmentCostsRaw = row.development_costs && typeof row.development_costs === 'object'
        ? row.development_costs
        : (row.developmentCosts && typeof row.developmentCosts === 'object' ? row.developmentCosts : {});
    const interimReportNumber = (() => {
        const n = Number(row.interim_report_number ?? row.interimReportNumber);
        return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
    })();

    return {
        projectReference: String(row.project_reference || row.projectReference || '').trim().toUpperCase(),
        reportType: String(row.report_type || row.reportType || 'initial').trim().toLowerCase() === 'interim' ? 'interim' : 'initial',
        interimReportNumber,
        programme: {
            worksStartMonth: String(programmeRaw.worksStartMonth || '').trim(),
            borrowerDurationMonths: String(programmeRaw.borrowerDurationMonths || '').trim(),
            surveyorDurationMonths: String(programmeRaw.surveyorDurationMonths || '').trim(),
            borrowerCompletionMonthYear: String(programmeRaw.borrowerCompletionMonthYear || '').trim(),
            surveyorCompletionMonthYear: String(programmeRaw.surveyorCompletionMonthYear || '').trim()
        },
        developmentCosts: normalizeDevelopmentCosts(developmentCostsRaw, interimReportNumber),
        completedAt: row.setup_completed_at || row.completedAt || null,
        storage: {
            bucket: row.storage_bucket || null,
            key: row.storage_key || null,
            region: row.storage_region || null
        }
    };
}

export default function Reports() {
    const { deviceType, isPhone } = useDeviceType();
    const reportVersionNaming = useMemo(() => getReportVersionNaming(), []);
    const [projects, setProjects] = useState([]);
    const [selected, setSelected] = useState(null);
    const [editFields, setEditFields] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [sortMode, setSortMode] = useState('reference-asc'); // default to project reference ascending
    const [search, setSearch] = useState('');

    const COMPLETED_KEY = 'monsurv_completed_projects';
    const [completedByRef, setCompletedByRef] = useState(() => {
        try {
            const raw = localStorage.getItem(COMPLETED_KEY);
            const parsed = raw ? JSON.parse(raw) : {};
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    });

    useEffect(() => {
        function refreshCompleted() {
            try {
                const raw = localStorage.getItem(COMPLETED_KEY);
                const parsed = raw ? JSON.parse(raw) : {};
                setCompletedByRef(parsed && typeof parsed === 'object' ? parsed : {});
            } catch {
                setCompletedByRef({});
            }
        }

        window.addEventListener('focus', refreshCompleted);
        window.addEventListener('storage', (e) => {
            if (e && e.key === COMPLETED_KEY) refreshCompleted();
        });
        return () => {
            window.removeEventListener('focus', refreshCompleted);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [projectSetupByRef, setProjectSetupByRef] = useState(() => ({}));
    const [projectSetupSaving, setProjectSetupSaving] = useState(false);

    const [projectSetupModal, setProjectSetupModal] = useState(() => ({
        open: false,
        step: 1,
        project: null,
        worksStartMonth: '',
        borrowerDurationMonths: '',
        surveyorDurationMonths: '',
        constructionBudgetRows: getDefaultSectionRows(),
        contingencyBudgetRows: getDefaultSectionRows(),
        contingencyBudgetTotal: '',
        professionalFeeBudgetRows: getDefaultSectionRows(),
        additionalCostBudgetRows: getDefaultSectionRows(),
        borrowerInputRelevant: false,
        borrowerInputAmount: '',
        previousDrawdownRows: getDefaultPreviousDrawdownRows(1)
    }));

    const [portalInitialReport, setPortalInitialReport] = useState(null);
    const [portalCostAssessments, setPortalCostAssessments] = useState(() => new Map());

    const [drawdownPrefillActuals, setDrawdownPrefillActuals] = useState(() => []);

    // Load portal-backed Initial Report tables + surveyor cost assessments (used for Drawdown baseline).
    useEffect(() => {
        const ref = String(selected?.reference || '').trim();
        if (!ref) {
            setPortalInitialReport(null);
            setPortalCostAssessments(new Map());
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const initialReportUrl = `${API_BASE_URL}/project/${encodeURIComponent(ref)}/initial-report?mode=surveyor`;
                const reportJson = await fetchJson(initialReportUrl, { method: 'GET' });
                if (!cancelled) setPortalInitialReport(reportJson || null);
            } catch {
                if (!cancelled) setPortalInitialReport(null);
            }
        })();

        (async () => {
            try {
                const qs = new URLSearchParams();
                qs.set('report_instance', 'initial');
                qs.set('mode', 'surveyor');
                const url = `${API_BASE_URL}/project/${encodeURIComponent(ref)}/cost-assessments?${qs.toString()}`;
                const json = await fetchJson(url, { method: 'GET' });
                if (cancelled) return;
                const items = Array.isArray(json?.items) ? json.items : [];
                const map = new Map();
                for (const r of items) {
                    const k = String(r?.item_key || '').trim();
                    if (!k) continue;
                    if (map.has(k)) continue; // keep latest value (backend sorted desc)
                    map.set(k, String(r?.surveyor_value ?? ''));
                }
                setPortalCostAssessments(map);
            } catch {
                if (!cancelled) setPortalCostAssessments(new Map());
            }
        })();

        return () => { cancelled = true; };
    }, [selected?.reference]);

    const [latestReportsByRef, setLatestReportsByRef] = useState(() => ({}));
    const [latestReportsLoading, setLatestReportsLoading] = useState(false);
    const [latestReportsError, setLatestReportsError] = useState('');

    const [snapshotOpen, setSnapshotOpen] = useState(false);
    const [snapshotLoading, setSnapshotLoading] = useState(false);
    const [snapshotError, setSnapshotError] = useState('');
    const [snapshotRef, setSnapshotRef] = useState('');
    const [snapshotText, setSnapshotText] = useState('');
    const [snapshotView, setSnapshotView] = useState('structured');
    const [snapshotExpandedSections, setSnapshotExpandedSections] = useState(() => ({}));

    const [projectAiSummary, setProjectAiSummary] = useState('');
    const [projectAiSummaryLoading, setProjectAiSummaryLoading] = useState(false);
    const [projectOfficeMapSettings, setProjectOfficeMapSettings] = useState(() => normalizeProjectOfficeMapSettings(null));
    const [projectOfficeMapLoading, setProjectOfficeMapLoading] = useState(false);
    const [selectedProjectOfficeId, setSelectedProjectOfficeId] = useState('');
    const [selectedProjectRouteMode, setSelectedProjectRouteMode] = useState('driving');

    // Phone UX: collapse AI narrative sections to reduce clutter.
    const [openAiNarrativeLabel, setOpenAiNarrativeLabel] = useState('');

    useEffect(() => {
        // Reset open section when switching projects.
        setOpenAiNarrativeLabel('');
    }, [selected?.reference]);

    useEffect(() => {
        setSelectedProjectRouteMode('driving');
    }, [selected?.reference]);

    useEffect(() => {
        const offices = Array.isArray(projectOfficeMapSettings?.offices) ? projectOfficeMapSettings.offices : [];
        setSelectedProjectOfficeId((prev) => {
            if (prev && offices.some((office) => office.id === prev)) return prev;
            return String(projectOfficeMapSettings?.primary_office_id || offices[0]?.id || '');
        });
    }, [projectOfficeMapSettings]);

    useEffect(() => {
        let cancelled = false;

        async function loadProjectOfficeMapSettings() {
            setProjectOfficeMapLoading(true);
            try {
                const json = await fetchJson(`${API_BASE_URL}/settings/office-map`, {
                    method: 'GET'
                });
                if (!cancelled) setProjectOfficeMapSettings(choosePreferredProjectOfficeMapSettings(json?.settings || {}, readOfficeMapSettingsCache()));
            } catch {
                if (!cancelled) setProjectOfficeMapSettings(choosePreferredProjectOfficeMapSettings(null, readOfficeMapSettingsCache()));
            } finally {
                if (!cancelled) setProjectOfficeMapLoading(false);
            }
        }

        loadProjectOfficeMapSettings();

        function handleOfficeMapSettingsUpdated(event) {
            if (cancelled) return;
            const nextSettings = event?.detail?.settings;
            setProjectOfficeMapSettings(normalizeProjectOfficeMapSettings(nextSettings || null));
            setProjectOfficeMapLoading(false);
        }

        window.addEventListener(OFFICE_MAP_SETTINGS_UPDATED_EVENT, handleOfficeMapSettingsUpdated);
        return () => {
            cancelled = true;
            window.removeEventListener(OFFICE_MAP_SETTINGS_UPDATED_EVENT, handleOfficeMapSettingsUpdated);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [projectActionsMenu, setProjectActionsMenu] = useState(() => ({ anchorEl: null, project: null }));
    const [expandedProjectCards, setExpandedProjectCards] = useState(() => ({}));

    const [healthIndexByRef, setHealthIndexByRef] = useState(() => loadHealthIndexCache());
    const [healthIndexLoadingByRef, setHealthIndexLoadingByRef] = useState(() => ({}));
    const [phiConfig, setPhiConfig] = useState(() => loadPhiConfig());

    const [outstandingInfoByRef, setOutstandingInfoByRef] = useState(() => ({}));
    const [outstandingInfoLoadingByRef, setOutstandingInfoLoadingByRef] = useState(() => ({}));
    const [questionnaireOutstandingByRef, setQuestionnaireOutstandingByRef] = useState(() => ({}));
    const [questionnaireOutstandingLoadingByRef, setQuestionnaireOutstandingLoadingByRef] = useState(() => ({}));
    const [questionnaireCustomRisksByRef, setQuestionnaireCustomRisksByRef] = useState(() => ({}));
    const [questionnaireCustomRisksLoadingByRef, setQuestionnaireCustomRisksLoadingByRef] = useState(() => ({}));

    const toggleProjectCard = useCallback((reference) => {
        const ref = String(reference || '').trim();
        if (!ref) return;
        setExpandedProjectCards((prev) => ({
            ...prev,
            [ref]: !prev?.[ref]
        }));
    }, []);

    function closeProjectSetupModal() {
        setProjectSetupModal({
            open: false,
            step: 1,
            project: null,
            worksStartMonth: '',
            borrowerDurationMonths: '',
            surveyorDurationMonths: '',
            constructionBudgetRows: getDefaultSectionRows(),
            contingencyBudgetRows: getDefaultSectionRows(),
            contingencyBudgetTotal: '',
            professionalFeeBudgetRows: getDefaultSectionRows(),
            additionalCostBudgetRows: getDefaultSectionRows(),
            borrowerInputRelevant: false,
            borrowerInputAmount: '',
            previousDrawdownRows: getDefaultPreviousDrawdownRows(1)
        });
    }

    function updateSetupSectionRow(sectionKey, index, key, value) {
        setProjectSetupModal((prev) => {
            const next = Array.isArray(prev?.[sectionKey]) ? prev[sectionKey].map((r) => ({ ...r })) : [];
            if (!next[index]) return prev;
            next[index][key] = value;
            return { ...prev, [sectionKey]: next };
        });
    }

    function addSetupSectionRow(sectionKey) {
        setProjectSetupModal((prev) => {
            const next = Array.isArray(prev?.[sectionKey]) ? prev[sectionKey].map((r) => ({ ...r })) : [];
            next.push({ item: '', budget: '', costToDate: '' });
            return { ...prev, [sectionKey]: next };
        });
    }

    function removeSetupSectionRow(sectionKey, index) {
        setProjectSetupModal((prev) => {
            const next = Array.isArray(prev?.[sectionKey]) ? prev[sectionKey].map((r) => ({ ...r })) : [];
            if (!next[index]) return prev;
            if (next.length <= 1) {
                return { ...prev, [sectionKey]: getDefaultSectionRows() };
            }
            next.splice(index, 1);
            return { ...prev, [sectionKey]: next };
        });
    }

    function updateSetupDrawdownRow(index, key, value) {
        setProjectSetupModal((prev) => {
            const next = Array.isArray(prev?.previousDrawdownRows) ? prev.previousDrawdownRows.map((r) => ({ ...r })) : [];
            if (!next[index]) return prev;
            next[index][key] = value;
            return { ...prev, previousDrawdownRows: next };
        });
    }

    function addSetupDrawdownRow() {
        setProjectSetupModal((prev) => {
            const next = Array.isArray(prev?.previousDrawdownRows) ? prev.previousDrawdownRows.map((r) => ({ ...r })) : [];
            next.push({ item: `Drawdown ${next.length + 1}`, amount: '' });
            return { ...prev, previousDrawdownRows: next };
        });
    }

    function removeSetupDrawdownRow(index) {
        setProjectSetupModal((prev) => {
            const next = Array.isArray(prev?.previousDrawdownRows) ? prev.previousDrawdownRows.map((r) => ({ ...r })) : [];
            if (!next[index]) return prev;
            if (next.length <= 1) {
                return { ...prev, previousDrawdownRows: getDefaultPreviousDrawdownRows(1) };
            }
            next.splice(index, 1);
            return {
                ...prev,
                previousDrawdownRows: next.map((row, idx) => ({
                    ...row,
                    item: String(row?.item || '').trim() || `Drawdown ${idx + 1}`
                }))
            };
        });
    }

    function submitProjectSetupStepOne() {
        const worksStartMonth = String(projectSetupModal?.worksStartMonth || '').trim();
        const borrowerDurationMonths = String(projectSetupModal?.borrowerDurationMonths || '').trim();
        const surveyorDurationMonths = String(projectSetupModal?.surveyorDurationMonths || '').trim();
        if (!worksStartMonth || !borrowerDurationMonths || !surveyorDurationMonths) {
            window.alert('Please complete all Programme fields before continuing.');
            return;
        }
        setProjectSetupModal((prev) => ({ ...prev, step: 2 }));
    }

    async function saveProjectSetupModal() {
        const project = projectSetupModal?.project;
        const ref = String(project?.reference || '').trim();
        if (!ref) return;
        if (projectSetupSaving) return;

        const refUpper = ref.toUpperCase();
        const sqlReportType = String(project?.inProgressReportType || '').trim().toLowerCase();
        const reportType = sqlReportType === 'interim' ? 'interim' : 'initial';
        const interimReportNumber = reportType === 'interim'
            ? (() => {
                const n = Number(project?.inProgressInterimNumber);
                return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
            })()
            : null;

        const sectionKeys = [
            'constructionBudgetRows',
            'contingencyBudgetRows',
            'professionalFeeBudgetRows',
            'additionalCostBudgetRows'
        ];
        const sectionCostToDateTotal = sectionKeys.reduce((sum, sectionKey) => {
            const rows = Array.isArray(projectSetupModal?.[sectionKey]) ? projectSetupModal[sectionKey] : [];
            return sum + sumSectionCostToDate(rows);
        }, 0);
        const drawdownTotal = (projectSetupModal?.previousDrawdownRows || []).reduce((sum, row) => sum + toMoneyNumber(row?.amount), 0);
        const borrowerInputTotal = projectSetupModal?.borrowerInputRelevant ? toMoneyNumber(projectSetupModal?.borrowerInputAmount) : 0;
        const drawdownAndBorrowerInputTotal = drawdownTotal + borrowerInputTotal;
        if (Math.abs(sectionCostToDateTotal - drawdownAndBorrowerInputTotal) > 0.009) {
            window.alert('Total Previous Drawdowns plus Borrower Input must equal the combined Cost to Date total across all budget tables.');
            return;
        }

        const payload = {
            programme: {
                worksStartMonth: String(projectSetupModal?.worksStartMonth || '').trim(),
                borrowerDurationMonths: String(projectSetupModal?.borrowerDurationMonths || '').trim(),
                surveyorDurationMonths: String(projectSetupModal?.surveyorDurationMonths || '').trim(),
                borrowerCompletionMonthYear: addMonthsToMonthYear(projectSetupModal?.worksStartMonth, projectSetupModal?.borrowerDurationMonths),
                surveyorCompletionMonthYear: addMonthsToMonthYear(projectSetupModal?.worksStartMonth, projectSetupModal?.surveyorDurationMonths)
            },
            developmentCosts: {
                constructionBudgetRows: normalizeSectionRows(projectSetupModal?.constructionBudgetRows),
                contingencyBudgetRows: normalizeSectionRows(projectSetupModal?.contingencyBudgetRows),
                contingencyBudgetTotal: String(projectSetupModal?.contingencyBudgetTotal ?? '').trim(),
                professionalFeeBudgetRows: normalizeSectionRows(projectSetupModal?.professionalFeeBudgetRows),
                additionalCostBudgetRows: normalizeSectionRows(projectSetupModal?.additionalCostBudgetRows),
                borrowerInputRelevant: Boolean(projectSetupModal?.borrowerInputRelevant),
                borrowerInputAmount: String(projectSetupModal?.borrowerInputAmount ?? '').trim(),
                previousDrawdownRows: Array.isArray(projectSetupModal?.previousDrawdownRows) && projectSetupModal.previousDrawdownRows.length
                    ? projectSetupModal.previousDrawdownRows.map((row, idx) => ({
                        item: String(row?.item || `Drawdown ${idx + 1}`).trim() || `Drawdown ${idx + 1}`,
                        amount: String(row?.amount ?? '').trim()
                    }))
                    : getDefaultPreviousDrawdownRows(interimReportNumber)
            },
            completedAt: new Date().toISOString(),
            report_type: reportType,
            interim_report_number: interimReportNumber
        };

        setProjectSetupSaving(true);
        try {
            const json = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(ref)}/setup`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const normalized = normalizeProjectSetupRecord(json?.item || {});
            if (normalized?.projectReference) {
                setProjectSetupByRef((prev) => ({
                    ...(prev || {}),
                    [normalized.projectReference]: normalized
                }));
            }

            const nextInterimNumber = reportType === 'interim'
                ? Number(interimReportNumber || 0) + 1
                : 1;

            setProjects((prev) => (Array.isArray(prev) ? prev.map((p) => {
                const pRef = String(p?.reference || '').trim().toUpperCase();
                if (pRef !== refUpper) return p;
                return {
                    ...p,
                    worksStartDate: payload.programme.worksStartMonth || p.worksStartDate,
                    duration: payload.programme.borrowerDurationMonths || p.duration,
                    inProgressReportType: 'interim',
                    inProgressInterimNumber: nextInterimNumber,
                    firstActionRequired: 'Information Portal'
                };
            }) : prev));

            closeProjectSetupModal();
            openInformationDashboard(ref, { page: 'interim', interim: nextInterimNumber });
        } catch (err) {
            window.alert(err?.message || 'Failed to save Project Setup.');
        } finally {
            setProjectSetupSaving(false);
        }
    }

    useEffect(() => {
        function refreshPhiConfig() {
            try {
                setPhiConfig(loadPhiConfig());
            } catch {
                setPhiConfig(loadPhiConfig());
            }
        }

        window.addEventListener('focus', refreshPhiConfig);
        window.addEventListener('monsurv_phi_config_changed', refreshPhiConfig);
        window.addEventListener('storage', (e) => {
            if (!e) return;
            if (e.key === PHI_CONFIG_KEY) refreshPhiConfig();
        });
        return () => {
            window.removeEventListener('focus', refreshPhiConfig);
            window.removeEventListener('monsurv_phi_config_changed', refreshPhiConfig);
        };
    }, []);

    // If PHI config changes, clear cached list scores to avoid showing stale values.
    useEffect(() => {
        try {
            setHealthIndexByRef({});
            saveHealthIndexCache({});
        } catch {
            // ignore
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(phiConfig)]);

    const deepLinkRef = useMemo(() => {
        try {
            const params = new URLSearchParams(window.location.search || '');
            const view = String(params.get('view') || '').trim().toLowerCase();
            const ref = String(params.get('ref') || params.get('reference') || '').trim();

            const openChangesRaw = String(params.get('openChanges') || params.get('open_changes') || '').trim();
            const openChanges = openChangesRaw === '1' || openChangesRaw.toLowerCase() === 'true' || openChangesRaw.toLowerCase() === 'yes';

            const reportLogIdRaw = String(params.get('reportLogId') || params.get('report_log_id') || '').trim();
            const reportLogId = reportLogIdRaw ? Number(reportLogIdRaw) : null;
            return {
                view,
                ref,
                refUpper: ref ? ref.toUpperCase() : '',
                reportLogId: Number.isFinite(reportLogId) ? reportLogId : null,
                openChanges
            };
        } catch {
            return { view: '', ref: '', refUpper: '', reportLogId: null, openChanges: false };
        }
    }, []);

    const didApplyDeepLinkSelection = useRef(false);

    useEffect(() => {
        if (didApplyDeepLinkSelection.current) return;
        if (!deepLinkRef.refUpper) return;
        if (!Array.isArray(projects) || projects.length === 0) return;

        const match = projects.find((p) => String(p?.reference || '').toUpperCase() === deepLinkRef.refUpper);
        if (match) setSelected(match);
        didApplyDeepLinkSelection.current = true;
    }, [projects, deepLinkRef]);

    const sCurveBaseline = useMemo(() => {
        const row = portalInitialReport?.tables?.ini_section_7_costs?.[0];
        if (!row) return { amount: null, source: null };
        const devGIA = portalInitialReport?.tables?.ini_section_6_development?.[0]?.gross_internal_area || null;
        return computeSCurveBaseline({ row, devGrossInternalArea: devGIA, valuesByKey: portalCostAssessments });
    }, [portalInitialReport, portalCostAssessments]);

    useEffect(() => {
        fetchJson(`${API_BASE_URL}/key_project_data`, { method: 'GET' })
            .then(data => {
                const list = Array.isArray(data) ? data : [];
                // Map backend fields to expected frontend fields
                const mapped = list.map(project => ({
                    ...project,
                    name: project.project_name,
                    siteAddress: project.site_address,
                    funder: project.funder,
                    leadSurveyor: project.lead_surveyor,
                    duration: project.project_duration,
                    reference: project.reference,
                    borrower: project.borrower_name,
                    borrowerCompany: project.borrower_company,
                    borrowerEmail: project.borrower_email,
                    facility: project.project_facility,
                    worksStartDate: project.works_start_month,
                    questionnaireSentAt: project.questionnaire_sent_at,
                    hasPortalData: Boolean(project.has_portal_data),
                    portalUpdatedAt: project.portal_updated_at || null,
                    inProgressReportType: String(project.in_progress_report_type || '').trim().toLowerCase(),
                    inProgressInterimNumber: Number.isFinite(Number(project.in_progress_interim_number))
                        ? Number(project.in_progress_interim_number)
                        : null,
                    firstActionRequired: String(project.first_action_required || '').trim()
                    // Add more mappings as needed
                }));
                setProjects(mapped);
            })
            .catch(() => setProjects([]));
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function loadProjectSetupRecords() {
            const refs = (Array.isArray(projects) ? projects : [])
                .map((p) => String(p?.reference || '').trim().toUpperCase())
                .filter(Boolean);

            if (!refs.length) {
                if (!cancelled) setProjectSetupByRef({});
                return;
            }

            try {
                const qs = new URLSearchParams();
                qs.set('references', refs.join(','));
                const json = await fetchJson(`${API_BASE_URL}/project-setup?${qs.toString()}`, { method: 'GET' });
                if (cancelled) return;
                const items = Array.isArray(json?.items) ? json.items : [];
                const map = {};
                for (const item of items) {
                    const normalized = normalizeProjectSetupRecord(item);
                    if (!normalized.projectReference) continue;
                    map[normalized.projectReference] = normalized;
                }
                setProjectSetupByRef(map);
            } catch {
                if (!cancelled) setProjectSetupByRef({});
            }
        }

        loadProjectSetupRecords();
        return () => {
            cancelled = true;
        };
    }, [projects]);

    const visibleProjectRefs = useMemo(() => {
        if (selected) return [];
        try {
            const list = getSortedFilteredProjects();
            return (Array.isArray(list) ? list : [])
                .map((p) => String(p?.reference || '').trim())
                .filter(Boolean);
        } catch {
            return [];
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, projects, completedByRef, search, sortMode]);

    useEffect(() => {
        let cancelled = false;

        async function fetchOne(ref) {
            if (!ref) return;

            if (!phiConfig?.enabled) return;

            const reportInstance = 'initial';
            const weights = getPhiWeightsForInstance(phiConfig, reportInstance);

            // Skip if we already have a fresh cached value.
            const factors = phiFactorsToQuery(phiConfig);
            const weightsSig = weightsToSig(weights);
            const cfgKey = `${ref}|${reportInstance}|weights:${weightsSig}|${String(phiConfig?.use_ai ? 1 : 0)}|${factors}`;
            const cached = healthIndexByRef?.[cfgKey];
            if (cached && cached.expires_at) {
                const exp = Date.parse(cached.expires_at);
                if (Number.isFinite(exp) && exp > Date.now()) return;
            }

            setHealthIndexLoadingByRef((m) => ({ ...(m || {}), [ref]: true }));
            try {
                const ttl = clampNumber(phiConfig?.ttl_minutes, 1, 240, 30);
                const aiFlag = phiConfig?.use_ai ? 1 : 0;
                const qs = new URLSearchParams();
                qs.set('mode', 'surveyor');
                qs.set('ai', String(aiFlag));
                qs.set('ttl_minutes', String(ttl));
                qs.set('report_instance', reportInstance);
                qs.set('weights', weightsToQueryValue(weights));
                if (factors) qs.set('factors', factors);

                const json = await fetchJson(
                    `${API_BASE_URL}/project/${encodeURIComponent(ref)}/health-index?${qs.toString()}`,
                    { method: 'GET' }
                );

                if (cancelled) return;

                setHealthIndexByRef((prev) => {
                    const next = { ...(prev || {}), [cfgKey]: json };
                    saveHealthIndexCache(next);
                    return next;
                });
            } catch {
                // Silent failure: we don't want to clutter the list UI.
            } finally {
                if (!cancelled) {
                    setHealthIndexLoadingByRef((m) => ({ ...(m || {}), [ref]: false }));
                }
            }
        }

        // Keep concurrency low so we don't overload the backend.
        async function run() {
            const refs = Array.isArray(visibleProjectRefs) ? visibleProjectRefs.slice(0, 50) : [];
            const queue = refs
                .filter((r) => !healthIndexLoadingByRef?.[r]);
            const concurrency = 3;
            let idx = 0;
            const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
                while (!cancelled && idx < queue.length) {
                    const ref = queue[idx++];
                    // eslint-disable-next-line no-await-in-loop
                    await fetchOne(ref);
                }
            });
            await Promise.all(workers);
        }

        if (visibleProjectRefs.length) run();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleProjectRefs.join('|'), JSON.stringify(phiConfig)]);

    // Fetch the "Outstanding Information" RAG for list items (shown as "Information Outstanding").
    useEffect(() => {
        if (selected) return;
        if (!visibleProjectRefs || visibleProjectRefs.length === 0) return;

        let cancelled = false;

        async function fetchOne(ref) {
            if (!ref) return;
            if (outstandingInfoByRef?.[ref]) return;

            setOutstandingInfoLoadingByRef((m) => ({ ...(m || {}), [ref]: true }));
            try {
                const data = await fetchRiskMatrix(ref);
                if (cancelled) return;
                const normalized = normalizeRiskMatrix(data);
                const out = normalized?.['Outstanding Information'] || { rating: '', comment: '' };
                setOutstandingInfoByRef((prev) => ({
                    ...(prev || {}),
                    [ref]: {
                        rating: String(out?.rating || '').trim(),
                        comment: String(out?.comment || '').trim()
                    }
                }));
            } catch {
                // Non-fatal: show unknown state.
            } finally {
                if (!cancelled) setOutstandingInfoLoadingByRef((m) => ({ ...(m || {}), [ref]: false }));
            }
        }

        async function run() {
            const refs = Array.isArray(visibleProjectRefs) ? visibleProjectRefs.slice(0, 50) : [];
            const queue = refs
                .filter((r) => !outstandingInfoLoadingByRef?.[r]);
            const concurrency = 3;
            let idx = 0;
            const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
                while (!cancelled && idx < queue.length) {
                    const ref = queue[idx++];
                    // eslint-disable-next-line no-await-in-loop
                    await fetchOne(ref);
                }
            });
            await Promise.all(workers);
        }

        run();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, visibleProjectRefs.join('|')]);

    useEffect(() => {
        const ref = String(selected?.reference || '').trim();
        if (!ref) return;
        if (questionnaireOutstandingLoadingByRef?.[ref]) return;

        let cancelled = false;

        async function loadSelectedOutstanding() {
            setQuestionnaireOutstandingLoadingByRef((m) => ({ ...(m || {}), [ref]: true }));
            try {
                const json = await fetchQuestionnaireOutstandingInfo(ref);
                if (cancelled) return;
                const items = Array.isArray(json?.items) ? json.items : [];
                setQuestionnaireOutstandingByRef((prev) => ({
                    ...(prev || {}),
                    [ref]: items
                }));
            } catch {
                if (cancelled) return;
                setQuestionnaireOutstandingByRef((prev) => ({
                    ...(prev || {}),
                    [ref]: []
                }));
            } finally {
                if (!cancelled) {
                    setQuestionnaireOutstandingLoadingByRef((m) => ({ ...(m || {}), [ref]: false }));
                }
            }
        }

        loadSelectedOutstanding();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected?.reference]);

    useEffect(() => {
        const ref = String(selected?.reference || '').trim();
        if (!ref) return;
        if (questionnaireCustomRisksLoadingByRef?.[ref]) return;

        let cancelled = false;

        async function loadSelectedCustomRisks() {
            setQuestionnaireCustomRisksLoadingByRef((m) => ({ ...(m || {}), [ref]: true }));
            try {
                const json = await fetchQuestionnaireCustomRisks(ref);
                if (cancelled) return;
                const items = Array.isArray(json?.items) ? json.items : [];
                setQuestionnaireCustomRisksByRef((prev) => ({
                    ...(prev || {}),
                    [ref]: items
                }));
            } catch {
                if (cancelled) return;
                setQuestionnaireCustomRisksByRef((prev) => ({
                    ...(prev || {}),
                    [ref]: []
                }));
            } finally {
                if (!cancelled) {
                    setQuestionnaireCustomRisksLoadingByRef((m) => ({ ...(m || {}), [ref]: false }));
                }
            }
        }

        loadSelectedCustomRisks();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected?.reference]);

    useEffect(() => {
        let cancelled = false;

        async function loadLatestReports() {
            if (!Array.isArray(projects) || projects.length === 0) {
                setLatestReportsByRef({});
                setLatestReportsError('');
                setLatestReportsLoading(false);
                return;
            }

            setLatestReportsLoading(true);
            setLatestReportsError('');

            try {
                const references = projects
                    .map((p) => String(p?.reference || '').trim())
                    .filter(Boolean);

                const json = await fetchJson(`${API_BASE_URL}/api/reports/latest`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ references })
                });

                if (cancelled) return;
                setLatestReportsByRef((json && json.items && typeof json.items === 'object') ? json.items : {});
            } catch (e) {
                if (cancelled) return;
                setLatestReportsError(e?.message || 'Failed to load latest report availability');
                setLatestReportsByRef({});
            } finally {
                if (!cancelled) setLatestReportsLoading(false);
            }
        }

        loadLatestReports();
        return () => { cancelled = true; };
    }, [projects]);

    async function openSnapshotSummary(reference) {
        const ref = String(reference || '').trim();
        if (!ref) return;

        setSnapshotOpen(true);
        setSnapshotRef(ref);
        setSnapshotText('');
        setSnapshotError('');
        setSnapshotLoading(true);
        setSnapshotView('structured');
        setSnapshotExpandedSections({});

        try {
            const json = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(ref)}/snapshot-summary?mode=surveyor`, {
                method: 'POST'
            });
            setSnapshotText(String(json?.summary || '').trim());
        } catch (e) {
            setSnapshotError(e?.message || 'Failed to generate snapshot summary');
        } finally {
            setSnapshotLoading(false);
        }
    }

    // Best-effort AI narrative for the Project Summary page (used in the new table).
    useEffect(() => {
        const ref = String(selected?.reference || '').trim();
        if (!ref) {
            setProjectAiSummary('');
            setProjectAiSummaryLoading(false);
            return;
        }

        let cancelled = false;
        setProjectAiSummaryLoading(true);

        (async () => {
            try {
                const json = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(ref)}/snapshot-summary?mode=surveyor`, {
                    method: 'POST'
                });
                if (cancelled) return;
                setProjectAiSummary(String(json?.summary || '').trim());
            } catch {
                if (cancelled) return;
                setProjectAiSummary('');
            } finally {
                if (!cancelled) setProjectAiSummaryLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [selected?.reference]);

    function openInformationDashboard(reference, options = {}) {
        const ref = String(reference || '').trim();
        if (!ref) return;
        const projectMatch = (Array.isArray(projects) ? projects : []).find((p) => String(p?.reference || '').trim() === ref);
        const inferredInProgressReportType = String(projectMatch?.inProgressReportType || '').trim().toLowerCase();
        const inferredInProgressInterimNumber = (() => {
            const n = Number(projectMatch?.inProgressInterimNumber);
            return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
        })();
        const nextParams = new URLSearchParams();
        nextParams.set('portal', 'surveyor');
        nextParams.set('reference', ref);
        const page = String(options?.page || '').trim().toLowerCase();
        if (page === 'initial' || page === 'interim' || page === 'drawdown' || page === 'cost_tables') {
            nextParams.set('page', page);
        } else if (inferredInProgressReportType === 'interim' && inferredInProgressInterimNumber) {
            nextParams.set('page', 'interim');
            nextParams.set('interim', String(inferredInProgressInterimNumber));
        }
        const interim = Number(options?.interim || 0);
        if (Number.isFinite(interim) && interim > 0) {
            nextParams.set('interim', String(Math.trunc(interim)));
        }
        const next = `?${nextParams.toString()}`;
        markInternalNavigation();
        try {
            window.location.assign(`${window.location.origin}${window.location.pathname}${next}`);
        } catch {
            window.location.href = next;
        }
    }

    function formatTimelineDate(value) {
        const raw = String(value ?? '').trim();
        if (!raw) return '';
        const d = new Date(raw);
        if (!Number.isFinite(d.getTime())) return raw;
        try {
            return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
        } catch {
            return raw;
        }
    }

    // --- Report Risk Matrix (per project, persistent, read-only on this page) ---
    // NOTE: must be declared before any useMemo that references it.
    const [riskMatrix, setRiskMatrix] = useState(() => normalizeRiskMatrix(null));
    const isFirstLoad = useRef(true);
    const riskMatrixReadOnly = true;
    const [riskAiLoading, setRiskAiLoading] = useState(false);
    const [riskAiError, setRiskAiError] = useState('');

    // --- Project Timeline (API-driven) ---
    const [projectTimelineItems, setProjectTimelineItems] = useState([]);
    const [projectTimelineLoading, setProjectTimelineLoading] = useState(false);
    const [projectTimelineError, setProjectTimelineError] = useState('');

    useEffect(() => {
        const ref = String(selected?.reference || '').trim();
        if (!ref) {
            setRiskMatrix(normalizeRiskMatrix(null));
            setRiskAiLoading(false);
            setRiskAiError('');
            isFirstLoad.current = true;
            return;
        }

        let cancelled = false;

        // Questionnaire-confirmed values are the only source of truth.
        // This view should read existing values only and must not auto-generate/persist risk data.
        (async () => {
            try {
                const existing = await fetchRiskMatrix(ref);
                if (cancelled) return;
                setRiskMatrix(normalizeRiskMatrix(existing));
            } catch {
                if (cancelled) return;
                setRiskMatrix(normalizeRiskMatrix(null));
            }
        })();

        isFirstLoad.current = true;
        return () => {
            cancelled = true;
        };
    }, [selected?.reference]);

    useEffect(() => {
        const ref = String(selected?.reference || '').trim();
        if (!ref) {
            setProjectTimelineItems([]);
            setProjectTimelineError('');
            setProjectTimelineLoading(false);
            return;
        }

        let cancelled = false;
        setProjectTimelineLoading(true);
        setProjectTimelineError('');

        (async () => {
            try {
                const json = await fetchJson(`${API_BASE_URL}/api/projects/${encodeURIComponent(ref)}/timeline?mode=surveyor`, {
                    method: 'GET'
                });
                if (cancelled) return;
                const items = Array.isArray(json?.items) ? json.items : [];
                setProjectTimelineItems(items);
            } catch (e) {
                if (cancelled) return;
                setProjectTimelineItems([]);
                setProjectTimelineError(String(e?.message || 'Failed to load timeline'));
            } finally {
                if (!cancelled) setProjectTimelineLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [selected?.reference]);

    // Save to backend when riskMatrix changes (disabled here because this view is read-only)
    useEffect(() => {
        const selectedRef = String(selected?.reference || '').trim();
        if (!selectedRef) return;
        if (riskMatrixReadOnly) return;
        if (isFirstLoad.current) {
            isFirstLoad.current = false;
            return;
        }

        const allRagPresent = RISK_CRITERIA_LIST.every((c) => (riskMatrix[c]?.rating || '').trim() !== '');
        if (!allRagPresent) return;

        const data = { project_reference: selectedRef };
        RISK_CRITERIA_LIST.forEach((criteria) => {
            const prefix = RISK_FIELD_MAP[criteria];
            if (!prefix) return;
            const val = riskMatrix[criteria] || { rating: '', comment: '' };
            data[`${prefix}_rag`] = val.rating;
            data[`${prefix}_comment`] = val.comment;
        });

        saveRiskMatrix(data).catch(() => { });
    }, [riskMatrix, selected?.reference, riskMatrixReadOnly]);

    const timelineSteps = useMemo(() => {
        const items = Array.isArray(projectTimelineItems) ? projectTimelineItems : [];
        return items
            .filter((it) => it && typeof it === 'object')
            .map((it) => {
                const key = String(it.milestoneKey || it.key || '').trim();
                const label = String(it.label || key || '').trim();
                const status = String(it.status || '').trim().toLowerCase();
                const completed = status === 'completed';
                const isAlert = status === 'alert';
                const dateRaw = it.date ? String(it.date).trim() : '';
                const dateType = String(it.dateType || '').trim().toLowerCase();
                const isEstimated = dateType === 'estimated';
                const dateLabelBase = dateRaw ? formatTimelineDate(dateRaw) : '';
                const dateLabel = dateLabelBase && isEstimated ? `Est. ${dateLabelBase}` : dateLabelBase;
                return {
                    key: key || label,
                    label,
                    status,
                    completed,
                    isAlert,
                    dateRaw,
                    dateType,
                    dateLabel
                };
            });
    }, [projectTimelineItems]);

    const programmeTimelineSteps = useMemo(() => {
        return (Array.isArray(timelineSteps) ? timelineSteps : [])
            .filter((s) => String(s?.key || '').toUpperCase().startsWith('PROG_'));
    }, [timelineSteps]);

    const coreTimelineSteps = useMemo(() => {
        return (Array.isArray(timelineSteps) ? timelineSteps : [])
            .filter((s) => !String(s?.key || '').toUpperCase().startsWith('PROG_'));
    }, [timelineSteps]);

    const aiNarrativeRows = useMemo(() => {
        const parsed = parseSnapshotSummaryText(projectAiSummary);
        const selectedRef = String(selected?.reference || '').trim();
        const questionnaireOutstandingItems = selectedRef
            ? (Array.isArray(questionnaireOutstandingByRef?.[selectedRef]) ? questionnaireOutstandingByRef[selectedRef] : null)
            : null;
        const questionnaireOutstandingLoading = selectedRef ? Boolean(questionnaireOutstandingLoadingByRef?.[selectedRef]) : false;
        const questionnaireCustomRisks = selectedRef
            ? (Array.isArray(questionnaireCustomRisksByRef?.[selectedRef]) ? questionnaireCustomRisksByRef[selectedRef] : null)
            : null;
        const questionnaireCustomRisksLoading = selectedRef ? Boolean(questionnaireCustomRisksLoadingByRef?.[selectedRef]) : false;

        const normalizeText = (v, fallback) => {
            const s = String(v || '').trim();
            return s || fallback;
        };

        const riskFor = (criteria) => {
            const key = String(criteria || '').trim();
            const rating = key ? String(riskMatrix?.[key]?.rating || '').trim() : '';
            return rating;
        };

        const outstandingInfoList = (() => {
            if (Array.isArray(questionnaireOutstandingItems)) {
                return questionnaireOutstandingItems
                    .map((item) => String(item?.label || '').trim())
                    .filter(Boolean)
                    .join('\n');
            }

            // Prefer parsed AI narrative if present; fallback to risk matrix comment.
            const fromAi = String(parsed?.risks?.['Outstanding Information'] || '').trim();
            if (fromAi) return fromAi;
            const comment = String(riskMatrix?.['Outstanding Information']?.comment || '').trim();
            return comment;
        })();

        const formatOutstandingInfoBullets = (value) => {
            const raw = String(value || '').replace(/\r\n/g, '\n').trim();
            if (!raw) return '';

            // Remove any leading RAG prefix (e.g., "Amber – ...") and any colour word at the start of a line.
            const cleaned = raw
                .split('\n')
                .map((ln) => stripLeadingRating(ln))
                .map((ln) => String(ln || '').replace(/^\s*(red|amber|green|yellow)\b\s*[:-–—]?\s*/i, '').trim())
                .filter(Boolean);

            // Normalise bullets: if no explicit bullets, force a single bullet.
            const hasExplicitBullets = cleaned.some((ln) => /^[-*•]\s+/.test(ln));
            const lines = hasExplicitBullets
                ? cleaned.map((ln) => ln.replace(/^\s*[*•]\s+/, '- ').replace(/^\s*-\s+/, '- '))
                : cleaned.map((ln) => `- ${ln}`);

            return lines.join('\n');
        };

        const stripLeadingRating = (s) => {
            // Convert "AMBER – foo" or "Amber - foo" to "foo" while preserving other text.
            const t = String(s || '').trim();
            const m = t.match(/^(red|amber|green|yellow)\s*[-–—:]\s*(.+)$/i);
            if (m) return String(m[2] || '').trim();
            return t;
        };

        const stripLeadingBullet = (s) => {
            const t = String(s || '').trim();
            // Some AI outputs (or our risk-line format) may start the narrative with a dash
            // when the RAG field is blank, e.g. "– Narrative...". Strip that.
            return t.replace(/^[-•–—]\s*/, '').trim();
        };

        const riskCommentFor = (criteria) => {
            return String(riskMatrix?.[criteria]?.comment || '').trim();
        };

        const customRiskRows = questionnaireCustomRisksLoading
            ? [{ label: 'Additional Risks', text: 'Loading additional risks...', risk: '' }]
            : (Array.isArray(questionnaireCustomRisks) ? questionnaireCustomRisks : [])
                .map((item, index) => {
                    const riskLabel = String(item?.risk || item?.label || '').trim();
                    const riskDetail = String(item?.riskDetail || item?.surveyorRiskDescription || item?.detail || '').trim();
                    const rag = normalizeRiskRating(String(item?.rag || item?.surveyorRiskRag || item?.finalDesignation || '').trim());
                    if (!riskLabel && !riskDetail) return null;
                    return {
                        label: riskLabel || `Additional Risk ${index + 1}`,
                        text: normalizeText(riskDetail, 'No risk detail provided.'),
                        risk: rag
                    };
                })
                .filter(Boolean);

        return [
            {
                label: 'Project Description',
                text: projectAiSummaryLoading
                    ? 'Generating Summary...'
                    : normalizeText(parsed?.project_description || parsed?.overview || projectAiSummary, 'No AI summary available yet.'),
                risk: ''
            },
            {
                label: 'Site Progress',
                text: normalizeText(riskCommentFor('Site'), 'Generating Summary...'),
                risk: riskFor('Site')
            },
            {
                label: 'Planning',
                text: normalizeText(riskCommentFor('Planning'), 'Generating Summary...'),
                risk: riskFor('Planning')
            },
            {
                label: 'Building Control',
                text: normalizeText(stripLeadingBullet(riskCommentFor('Building Control') || riskCommentFor('Planning')), 'Generating Summary...'),
                risk: riskFor('Building Control') || riskFor('Planning')
            },
            {
                label: 'Building Warranty',
                text: normalizeText(stripLeadingBullet(riskCommentFor('Building Warranty') || riskCommentFor('Contract')), 'Generating Summary...'),
                risk: riskFor('Building Warranty') || riskFor('Contract')
            },
            {
                label: 'Procurement',
                text: normalizeText(riskCommentFor('Procurement') || riskCommentFor('Contract'), 'Generating Summary...'),
                risk: riskFor('Procurement') || riskFor('Contract')
            },
            {
                label: 'Programme',
                text: normalizeText(riskCommentFor('Programme'), 'Generating Summary...'),
                risk: riskFor('Programme')
            },
            {
                label: 'Insurances',
                text: normalizeText(riskCommentFor('Insurances'), 'Generating Summary...'),
                risk: riskFor('Insurances')
            },
            {
                label: 'Costs',
                text: normalizeText(riskCommentFor('Costs'), 'Generating Summary...'),
                risk: riskFor('Costs')
            },
            ...customRiskRows,
            {
                label: 'Outstanding Info',
                text: questionnaireOutstandingLoading
                    ? 'Loading outstanding information...'
                    : normalizeText(formatOutstandingInfoBullets(outstandingInfoList), 'No outstanding information recorded.'),
                risk: ''
            }
        ];
    }, [
        riskMatrix,
        projectAiSummary,
        projectAiSummaryLoading,
        questionnaireOutstandingByRef,
        questionnaireOutstandingLoadingByRef,
        questionnaireCustomRisksByRef,
        questionnaireCustomRisksLoadingByRef,
        selected?.reference
    ]);

    const selectedProjectAddress = useMemo(() => {
        return String(selected?.siteAddress || selected?.site_address || '').trim();
    }, [selected?.siteAddress, selected?.site_address]);

    const selectedProjectMapItems = useMemo(() => {
        if (!selectedProjectAddress) return [];
        return [{
            name: String(selected?.name || selected?.reference || 'Project Site').trim() || 'Project Site',
            address: selectedProjectAddress,
            lat: selected?.lat ?? selected?.latitude ?? null,
            lng: selected?.lng ?? selected?.longitude ?? selected?.lon ?? null
        }];
    }, [selected?.name, selected?.reference, selected?.lat, selected?.latitude, selected?.lng, selected?.longitude, selected?.lon, selectedProjectAddress]);

    const selectedProjectOffice = useMemo(() => {
        const offices = Array.isArray(projectOfficeMapSettings?.offices) ? projectOfficeMapSettings.offices : [];
        const matching = offices.find((office) => office.id === selectedProjectOfficeId);
        return matching || offices[0] || null;
    }, [projectOfficeMapSettings, selectedProjectOfficeId]);

    const selectedProjectOfficeName = useMemo(() => {
        return String(selectedProjectOffice?.office_location || '').trim() || 'Organisation Office';
    }, [selectedProjectOffice]);

    const selectedProjectOfficeAddress = useMemo(() => {
        return String(selectedProjectOffice?.address || '').trim();
    }, [selectedProjectOffice]);

    const snapshotStructured = useMemo(() => {
        const parsed = parseSnapshotSummaryText(snapshotText);
        const sections = [
            { key: 'project_description', title: 'Project Description', value: parsed.project_description },
            { key: 'site_progress', title: 'Site Progress', value: parsed.site_progress },
            { key: 'overview', title: 'Project Overview', value: parsed.overview },
            { key: 'programme', title: 'Programme', value: parsed.programme },
            { key: 'costs', title: 'Costs / Drawdown', value: parsed.costs },
            { key: 'actions', title: 'Key Actions / Next Steps', value: parsed.actions }
        ].filter((section) => String(section.value || '').trim());

        const risks = Object.entries(parsed?.risks || {})
            .map(([label, rawValue]) => {
                const parsedRisk = parseSnapshotRiskValue(rawValue);
                return {
                    label,
                    rating: parsedRisk.rating,
                    narrative: parsedRisk.narrative,
                    rawValue: String(rawValue || '').trim()
                };
            })
            .filter((risk) => risk.rating || risk.narrative || risk.rawValue);

        const takeaways = [];
        for (const section of sections) {
            const preview = getSnapshotPreviewLine(section.value);
            if (!preview) continue;
            takeaways.push({ key: section.key, label: section.title, text: preview });
            if (takeaways.length >= 4) break;
        }

        if (takeaways.length < 4) {
            for (const risk of risks) {
                if (!risk.rating) continue;
                takeaways.push({ key: `risk-${risk.label}`, label: 'Risk Signal', text: `${risk.label}: ${risk.rating}` });
                if (takeaways.length >= 4) break;
            }
        }

        return {
            sections,
            risks,
            takeaways,
            hasStructuredContent: Boolean(sections.length || risks.length)
        };
    }, [snapshotText]);

    const toggleSnapshotSection = useCallback((sectionKey) => {
        setSnapshotExpandedSections((prev) => ({
            ...prev,
            [sectionKey]: !prev?.[sectionKey]
        }));
    }, []);

    // Populate the 3 photo placeholders (Project / Extra 2 / Extra 3) using existing site photos stored in SQL.
    const [sitePhotos, setSitePhotos] = useState([]);
    const [sitePhotoIndex, setSitePhotoIndex] = useState(0);
    const [sitePhotoLoading, setSitePhotoLoading] = useState(false);
    useEffect(() => {
        const ref = selected?.reference;
        if (!ref) return;

        let cancelled = false;

        (async () => {
            try {
                const data = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(ref)}/site-photos`, {
                    method: 'GET'
                });
                const photos = Array.isArray(data?.photos) ? data.photos : [];
                const mapped = photos
                    .map((p) => {
                        const u = String(p?.url || '').trim();
                        if (!u) return null;
                        return {
                            url: u.startsWith('http') ? u : `${API_BASE_URL}${u}`,
                            created_at: p?.created_at || null,
                            taken_at: p?.taken_at || null,
                            taken_at_raw: p?.taken_at_raw || null,
                            file_name: p?.file_name || null,
                            id: p?.id || null,
                        };
                    })
                    .filter(Boolean);
                const urls = mapped.map((p) => p.url);

                if (!cancelled) {
                    if (urls.length) setSitePhotoLoading(true);
                    setSitePhotos(mapped);
                    setSitePhotoIndex((prev) => {
                        const next = Number.isFinite(Number(prev)) ? Number(prev) : 0;
                        if (!urls.length) return 0;
                        if (next < 0) return 0;
                        if (next >= urls.length) return 0;
                        return next;
                    });
                }

                if (cancelled) return;
                setSelected((prev) => {
                    if (!prev || prev.reference !== ref) return prev;
                    return {
                        ...prev,
                        imageUrl: prev.imageUrl || urls[0],
                        imageUrl2: prev.imageUrl2 || urls[1],
                        imageUrl3: prev.imageUrl3 || urls[2]
                    };
                });
            } catch (e) {
                // Non-fatal: keep placeholders if photos cannot be loaded.
                if (!cancelled) {
                    setSitePhotos([]);
                    setSitePhotoIndex(0);
                    setSitePhotoLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [selected?.reference]);

    const sitePhotoUrls = useMemo(() => {
        return (Array.isArray(sitePhotos) ? sitePhotos : []).map((p) => p?.url).filter(Boolean);
    }, [sitePhotos]);

    const activeSitePhoto = useMemo(() => {
        const photos = Array.isArray(sitePhotos) ? sitePhotos : [];
        if (photos.length && photos[sitePhotoIndex]) return photos[sitePhotoIndex];
        return null;
    }, [sitePhotos, sitePhotoIndex]);

    const activeSitePhotoUrl = useMemo(() => {
        const url = String(activeSitePhoto?.url || '').trim();
        if (url) return url;
        return selected?.imageUrl || '';
    }, [activeSitePhoto?.url, selected?.imageUrl]);

    // Smooth carousel: show a lightweight loading state and prefetch adjacent images.
    useEffect(() => {
        const urls = Array.isArray(sitePhotoUrls) ? sitePhotoUrls : [];
        const src = String(activeSitePhotoUrl || '').trim();
        if (!src) {
            setSitePhotoLoading(false);
            return;
        }

        // Prefetch next/prev to make cycling feel instant.
        if (urls.length > 1) {
            const nextIdx = (sitePhotoIndex + 1) % urls.length;
            const prevIdx = (sitePhotoIndex - 1 + urls.length) % urls.length;
            for (const u of [urls[nextIdx], urls[prevIdx]]) {
                try {
                    const img = new Image();
                    img.src = u;
                } catch {
                    // ignore
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSitePhotoUrl, sitePhotoIndex, sitePhotoUrls]);

    const canCycleSitePhotos = Array.isArray(sitePhotoUrls) && sitePhotoUrls.length > 1;
    const goPrevSitePhoto = () => {
        const urls = Array.isArray(sitePhotoUrls) ? sitePhotoUrls : [];
        if (urls.length < 2) return;
        setSitePhotoLoading(true);
        setSitePhotoIndex((i) => (i - 1 + urls.length) % urls.length);
    };
    const goNextSitePhoto = () => {
        const urls = Array.isArray(sitePhotoUrls) ? sitePhotoUrls : [];
        if (urls.length < 2) return;
        setSitePhotoLoading(true);
        setSitePhotoIndex((i) => (i + 1) % urls.length);
    };

    // Phone UX: tap photo to open a full-screen lightbox.
    const [photoLightboxOpen, setPhotoLightboxOpen] = useState(false);
    const [photoLightboxIndex, setPhotoLightboxIndex] = useState(0);

    const phonePhotoUrls = useMemo(() => {
        const fromDb = Array.isArray(sitePhotoUrls) ? sitePhotoUrls.filter(Boolean) : [];
        if (fromDb.length) return fromDb;

        const fromSelected = [selected?.imageUrl, selected?.imageUrl2, selected?.imageUrl3]
            .filter(Boolean)
            .map((u) => String(u));
        if (fromSelected.length) return fromSelected;

        return [
            svgPlaceholderDataUri('Project Photo'),
            svgPlaceholderDataUri('Photo 2'),
            svgPlaceholderDataUri('Photo 3')
        ];
    }, [sitePhotoUrls, selected?.imageUrl, selected?.imageUrl2, selected?.imageUrl3]);

    const canCycleLightbox = isPhone && Array.isArray(phonePhotoUrls) && phonePhotoUrls.length > 1;

    const openLightboxAt = (idx) => {
        if (!isPhone) return;
        const urls = Array.isArray(phonePhotoUrls) ? phonePhotoUrls : [];
        if (!urls.length) return;
        const n = urls.length;
        const safe = Number.isFinite(Number(idx)) ? Number(idx) : 0;
        const clamped = ((safe % n) + n) % n;
        setPhotoLightboxIndex(clamped);
        setPhotoLightboxOpen(true);
    };

    const closeLightbox = () => {
        setPhotoLightboxOpen(false);
    };

    useEffect(() => {
        // Close when switching projects.
        setPhotoLightboxOpen(false);
        setPhotoLightboxIndex(0);
    }, [selected?.reference]);

    useEffect(() => {
        if (!photoLightboxOpen || !isPhone) return;
        function onKeyDown(e) {
            if (!e) return;
            if (e.key === 'Escape') {
                e.preventDefault();
                closeLightbox();
                return;
            }
            if (!canCycleLightbox) return;
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setPhotoLightboxIndex((i) => {
                    const n = phonePhotoUrls.length || 1;
                    return (i - 1 + n) % n;
                });
            }
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                setPhotoLightboxIndex((i) => {
                    const n = phonePhotoUrls.length || 1;
                    return (i + 1) % n;
                });
            }
        }

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [photoLightboxOpen, isPhone, canCycleLightbox, phonePhotoUrls.length]);

    const [reportLog, setReportLog] = useState([]);
    const [reportLogSource, setReportLogSource] = useState('db');
    const [deletingReportLogId, setDeletingReportLogId] = useState(null);

    const isAdmin = (() => {
        try {
            const tok = String(localStorage.getItem('monsurv_auth_token') || '').trim();
            return tryGetDashboardRoleFromToken(tok) === 'admin';
        } catch {
            return false;
        }
    })();

    const didScrollDeepLinkReportLog = useRef(false);
    const didOpenDeepLinkChanges = useRef(false);

    const [changesModal, setChangesModal] = useState(() => ({
        open: false,
        loading: false,
        error: '',
        title: '',
        meta: null,
        items: []
    }));

    const [namePromptOpen, setNamePromptOpen] = useState(false);
    const [namePromptKind, setNamePromptKind] = useState('');
    const [namePromptIdx, setNamePromptIdx] = useState(-1);
    const [namePromptTitle, setNamePromptTitle] = useState('');
    const [namePromptValue, setNamePromptValue] = useState('');
    const [namePromptError, setNamePromptError] = useState('');

    // Load report log for selected project (DB-backed; localStorage fallback)
    useEffect(() => {
        const ref = selected?.reference;
        if (!ref) {
            setReportLog([]);
            setReportLogSource('db');
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const reportLogUrl = `${API_BASE_URL}/project/${encodeURIComponent(ref)}/report-log` +
                    (process.env.NODE_ENV === 'production' ? '' : '?mode=surveyor');
                const json = await fetchJson(reportLogUrl, { method: 'GET' });
                if (cancelled) return;
                setReportLog(normalizeReportLogItems(json?.items));
                setReportLogSource('db');
                if (selected?.id) clearReportLogCache(selected.id);
            } catch (e) {
                if (cancelled) return;
                // Fallback to legacy localStorage storage.
                if (selected?.id) {
                    setReportLog(loadReportLog(selected.id));
                    setReportLogSource('local');
                } else {
                    setReportLog([]);
                    setReportLogSource('local');
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [selected?.reference, selected?.id]);

    async function reloadReportLogFromDb(reference) {
        const ref = String(reference || '').trim();
        if (!ref) return;
        const reportLogUrl = `${API_BASE_URL}/project/${encodeURIComponent(ref)}/report-log` +
            (process.env.NODE_ENV === 'production' ? '' : '?mode=surveyor');
        const json = await fetchJson(reportLogUrl, { method: 'GET' });
        setReportLog(normalizeReportLogItems(json?.items));
        setReportLogSource('db');
        if (selected?.id) clearReportLogCache(selected.id);
    }

    async function handleDeleteReportLogRow(idx) {
        if (!isAdmin) return;
        if (reportLogSource !== 'db') return;
        const ref = String(selected?.reference || '').trim();
        const row = reportLog?.[idx] || null;
        const rowId = row?.id;
        if (!ref || rowId == null) return;

        const label = `${row?.type || 'Report'} ${formatReportVersion(row?.revision, reportVersionNaming) || row?.revision || ''}`.trim();
        const ok = window.confirm(
            `Admin: delete this report version?\n\n${label}\n\nThis permanently removes the stored DOCX (and any related PDFs) so future generations create a fresh file.`
        );
        if (!ok) return;

        try {
            setDeletingReportLogId(Number(rowId));
            await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(ref)}/report-log/${encodeURIComponent(String(rowId))}`, {
                method: 'DELETE'
            });
            // Optimistic remove, then re-sync from backend.
            setReportLog((log) => log.filter((r) => Number(r?.id) !== Number(rowId)));
            await reloadReportLogFromDb(ref);
        } catch (e) {
            window.alert(e?.message || 'Failed to delete report version');
        } finally {
            setDeletingReportLogId(null);
        }
    }

    // If opened from a notification deep-link, scroll the target report-log row into view.
    useEffect(() => {
        if (didScrollDeepLinkReportLog.current) return;
        const targetId = deepLinkRef?.reportLogId;
        if (!Number.isFinite(Number(targetId))) return;
        if (!selected?.reference) return;
        if (!Array.isArray(reportLog) || reportLog.length === 0) return;

        const el = document.getElementById(`report-log-row-${Number(targetId)}`);
        if (!el || typeof el.scrollIntoView !== 'function') return;

        try {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } catch {
            el.scrollIntoView();
        }

        didScrollDeepLinkReportLog.current = true;
    }, [deepLinkRef, reportLog, selected?.reference]);

    const handleViewChanges = useCallback(async (idx) => {
        if (reportLogSource !== 'db') return;
        const ref = String(selected?.reference || '').trim();
        const row = reportLog?.[idx] || null;
        const rowId = row?.id;
        if (!ref || rowId == null) return;

        const title = `Changes since previous revision — ${row?.type || 'Report'} ${formatReportVersion(row?.revision, reportVersionNaming) || row?.revision || ''}`.trim();
        setChangesModal({ open: true, loading: true, error: '', title, meta: null, items: [] });

        try {
            const qs = new URLSearchParams();
            qs.set('limit', '50');
            if (process.env.NODE_ENV !== 'production') qs.set('mode', 'surveyor');
            const url = `${API_BASE_URL}/project/${encodeURIComponent(ref)}/report-log/${encodeURIComponent(String(rowId))}/changes?${qs.toString()}`;
            const json = await fetchJson(url, { method: 'GET' });

            if (json?.reason === 'no_previous_revision') {
                setChangesModal({
                    open: true,
                    loading: false,
                    error: 'No previous revision found for this report (nothing to compare against).',
                    title,
                    meta: json || null,
                    items: []
                });
                return;
            }

            const items = Array.isArray(json?.items) ? json.items : [];
            setChangesModal({ open: true, loading: false, error: '', title, meta: json || null, items });
        } catch (e) {
            setChangesModal({ open: true, loading: false, error: e?.message || 'Failed to load changes', title, meta: null, items: [] });
        }
    }, [reportLogSource, selected?.reference, reportLog, reportVersionNaming]);

    // If opened from a report notification, auto-open the changes modal for that report log row.
    useEffect(() => {
        if (didOpenDeepLinkChanges.current) return;
        if (!deepLinkRef?.openChanges) return;
        if (reportLogSource !== 'db') return;
        const targetId = deepLinkRef?.reportLogId;
        if (!Number.isFinite(Number(targetId))) return;
        if (!selected?.reference) return;
        if (!Array.isArray(reportLog) || reportLog.length === 0) return;

        const idx = reportLog.findIndex((r) => Number(r?.id) === Number(targetId));
        if (idx < 0) return;

        didOpenDeepLinkChanges.current = true;
        void handleViewChanges(idx);
    }, [deepLinkRef, reportLog, reportLogSource, selected?.reference, handleViewChanges]);

    // Save report log to localStorage only when using legacy mode.
    useEffect(() => {
        if (reportLogSource !== 'local') return;
        if (selected && selected.id) {
            saveReportLog(selected.id, reportLog);
        }
    }, [reportLog, selected, reportLogSource]);

    function handleFieldChange(key, value) {
        setEditFields(fields => ({ ...fields, [key]: value }));
    }

    function handleBack() {
        setSelected(null);
    }

    function getLeadSurveyorName(leadSurveyorId) {
        return leadSurveyorId || '';
    }

    function getFunderName(funderDetails) {
        // If the project stores the funder name directly, just return it
        if (!funderDetails) return '';
        return funderDetails; // In this app, funderDetails is the funder name (e.g., 'MSP Capital')
    }

    function formatCurrency(value) {
        if (value === undefined || value === null || value === '') return '';
        const num = Number(value);
        if (isNaN(num)) return value;
        return '£' + num.toLocaleString('en-GB');
    }

    function handleEdit() {
        setIsEditing(true);
        // Pre-fill editFields with current selected values
        const initial = {};
        summaryFields.forEach(f => {
            if (f.key !== 'borrower' && f.key !== 'borrowerCompany' && f.key !== 'borrowerEmail') return;
            initial[f.key] = selected[f.key] || '';
        });
        setEditFields(initial);
    }

    async function handleSave() {
        if (!selected) return;
        const updatedProject = { ...selected, ...editFields };

        // Best-effort persist to backend for fields needed by the portal/email.
        // (Front-end can still work locally if backend/schema isn't ready.)
        try {
            if (updatedProject.reference) {
                await fetchJson(`${API_BASE_URL}/key_project_data/reference/${encodeURIComponent(updatedProject.reference)}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        borrower_name: updatedProject.borrower,
                        borrower_company: updatedProject.borrowerCompany,
                        borrower_email: updatedProject.borrowerEmail
                    })
                });
            }
        } catch (e) { }

        setProjects(projects => {
            const updated = projects.map(p => (p.id === selected.id ? updatedProject : p));
            return updated;
        });
        setSelected(updatedProject);
        setIsEditing(false);
    }

    // Determine which fields are editable: Borrower Name, Project Facility, Project Duration, and Works Start Date if missing or in edit mode
    function isFieldEditable(f) {
        // Only borrower fields are editable here; facility/duration/start are sourced from ini_* (Initial Report) data.
        if (f.key !== 'borrower' && f.key !== 'borrowerCompany' && f.key !== 'borrowerEmail') return false;
        return isEditing || !selected[f.key];
    }

    const summaryFromInitialReport = useMemo(() => {
        const programmeRow = portalInitialReport?.tables?.ini_section_8_programme?.[0] || null;
        const worksStartYm = isoToYearMonth(programmeRow?.start_date) || null;
        const durationMonthsRaw = programmeRow?.duration_months;
        const durationWeeksRaw = programmeRow?.duration_weeks;

        const durationMonths = (durationMonthsRaw !== null && durationMonthsRaw !== undefined && String(durationMonthsRaw) !== '')
            ? Number(durationMonthsRaw)
            : (durationWeeksRaw !== null && durationWeeksRaw !== undefined && String(durationWeeksRaw) !== ''
                ? Math.round(Number(durationWeeksRaw) / 4.345)
                : null);

        const facilityConfirmedRaw = portalCostAssessments?.get ? portalCostAssessments.get('facility_confirmed') : '';
        const facilityAmountRaw = portalCostAssessments?.get ? portalCostAssessments.get('facility_amount') : '';

        const facilityConfirmed = String(facilityConfirmedRaw ?? '').trim().toLowerCase();
        const isFacilityConfirmed = facilityConfirmed === 'true' || facilityConfirmed === '1' || facilityConfirmed === 'yes' || facilityConfirmed === 'y' || facilityConfirmed === 'on';
        const facilityAmountNum = facilityAmountRaw !== null && facilityAmountRaw !== undefined && String(facilityAmountRaw).trim() !== ''
            ? Number(String(facilityAmountRaw).replace(/[^0-9.-]/g, ''))
            : null;

        const facilityDisplay = (isFacilityConfirmed && Number.isFinite(facilityAmountNum) && facilityAmountNum > 0)
            ? formatCurrency(facilityAmountNum)
            : 'TBC';

        const worksStartDisplay = worksStartYm || 'TBC';
        const durationDisplay = Number.isFinite(durationMonths) && durationMonths > 0 ? String(durationMonths) : 'TBC';

        return {
            facilityDisplay,
            worksStartYm,
            worksStartDisplay,
            durationMonths: (Number.isFinite(durationMonths) && durationMonths > 0) ? durationMonths : null,
            durationDisplay
        };
    }, [portalInitialReport, portalCostAssessments]);

    function parseMoneyLoose(v) {
        const s = String(v ?? '').trim();
        if (!s) return 0;
        const cleaned = s.replace(/[^0-9.-]/g, '');
        const n = Number(cleaned);
        return Number.isFinite(n) ? n : 0;
    }

    function computeInterimDrawdownClaim(answers) {
        const a = answers && typeof answers === 'object' ? answers : null;
        if (!a) return null;

        const drawdownCosts = a.drawdown_costs && typeof a.drawdown_costs === 'object' ? a.drawdown_costs : null;
        const ddConstruction = drawdownCosts?.dd_construction && typeof drawdownCosts.dd_construction === 'object' ? drawdownCosts.dd_construction : null;

        let construction = 0;
        if (ddConstruction && typeof ddConstruction === 'object') {
            for (const v of Object.values(ddConstruction)) {
                construction += parseMoneyLoose(v);
            }
        } else if (a.construction_cost_claim != null) {
            construction = parseMoneyLoose(a.construction_cost_claim);
        }

        let contingency = 0;
        if (a.contingency_claim != null) {
            contingency = parseMoneyLoose(a.contingency_claim);
        } else if (Array.isArray(drawdownCosts?.contingency_claims)) {
            contingency = drawdownCosts.contingency_claims.reduce((acc, c) => acc + parseMoneyLoose(c?.value), 0);
        }

        const total = construction + contingency;
        return Number.isFinite(total) ? Math.max(0, total) : null;
    }

    // Prefill Actual Drawdown (DDn) from Interim Report n: Construction claim + Contingency claim.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        let cancelled = false;

        async function run() {
            try {
                if (!selected || !selected.reference) {
                    setDrawdownPrefillActuals([]);
                    return;
                }
                const ref = selected.reference;

                const subs = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(ref)}/interim-report/submissions?mode=surveyor`, { method: 'GET' });
                const nums = (Array.isArray(subs?.items) ? subs.items : [])
                    .map((it) => Number(it?.interim_number || 0))
                    .filter((n) => Number.isFinite(n) && n > 0)
                    .sort((a, b) => a - b);

                if (!nums.length) {
                    if (!cancelled) setDrawdownPrefillActuals([]);
                    return;
                }

                const auto = [];
                await Promise.all(nums.map(async (n) => {
                    try {
                        const ir = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(ref)}/interim-report?mode=surveyor&interim=${encodeURIComponent(String(n))}`, { method: 'GET' });
                        const total = computeInterimDrawdownClaim(ir?.answers);
                        if (total === null || total === undefined) return;
                        if (!Number.isFinite(Number(total))) return;
                        auto[n - 1] = total;
                    } catch {
                        // ignore individual missing/failed interim
                    }
                }));

                if (!cancelled) setDrawdownPrefillActuals(auto);
            } catch {
                if (!cancelled) setDrawdownPrefillActuals([]);
            }
        }

        run();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected?.reference]);

    function getSortedFilteredProjects() {
        let filtered = projects;

        // Keep in sync with the Instructions page "Existing List" by excluding completed projects.
        filtered = filtered.filter((p) => {
            const ref = String(p?.reference || '').trim();
            if (!ref) return true;
            return !completedByRef?.[ref];
        });

        if (search.trim()) {
            const s = search.trim().toLowerCase();
            filtered = filtered.filter(p =>
                (p.name || '').toLowerCase().includes(s) ||
                (p.reference || '').toLowerCase().includes(s)
            );
        }
        if (sortMode === 'alpha') {
            filtered = [...filtered].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        } else if (sortMode === 'reference-desc') {
            filtered = [...filtered].sort((a, b) => (b.reference || '').localeCompare(a.reference || ''));
        } else if (sortMode === 'reference-asc') {
            filtered = [...filtered].sort((a, b) => (a.reference || '').localeCompare(b.reference || ''));
        }
        return filtered;
    }

    function handleQAChange(idx) {
        if (reportLog[idx].qaChecked) return;
        setNamePromptKind('qa');
        setNamePromptIdx(idx);
        setNamePromptTitle('Enter the name of the QA colleague');
        setNamePromptValue('');
        setNamePromptError('');
        setNamePromptOpen(true);
    }
    function handleIssueChange(idx) {
        if (reportLog[idx].issueChecked) return;
        setNamePromptKind('issue');
        setNamePromptIdx(idx);
        setNamePromptTitle('Enter the name of the Issuing colleague');
        setNamePromptValue('');
        setNamePromptError('');
        setNamePromptOpen(true);
    }

    async function applyReportLogName(kind, idx, rawName) {
        const name = String(rawName || '').trim();
        if (!name) return;
        if (idx < 0 || idx >= reportLog.length) return;

        if (kind === 'qa') {
            if (reportLog[idx].qaChecked) return;

            if (reportLogSource === 'db' && selected?.reference && reportLog[idx]?.id != null) {
                const ref = selected.reference;
                const rowId = reportLog[idx].id;

                try {
                    const json = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(ref)}/report-log/${rowId}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ qa_checked: true, qa_name: name })
                    });
                    const updated = normalizeReportLogItems([json?.item])[0];
                    setReportLog((log) => log.map((row, i) => (i === idx ? (updated || row) : row)));
                } catch (e) {
                    window.alert(e?.message || 'Failed to update QA fields');
                }
            } else {
                setReportLog(log => log.map((row, i) => i === idx ? { ...row, qaChecked: true, qaName: name } : row));
            }
        }

        if (kind === 'issue') {
            if (reportLog[idx].issueChecked) return;

            const todayIso = new Date().toISOString().slice(0, 10);
            const dateStr = formatDDMMYY(todayIso);

            if (reportLogSource === 'db' && selected?.reference && reportLog[idx]?.id != null) {
                const ref = selected.reference;
                const rowId = reportLog[idx].id;

                try {
                    const json = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(ref)}/report-log/${rowId}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ issue_checked: true, issued_by: name, issue_date: todayIso })
                    });
                    const updated = normalizeReportLogItems([json?.item])[0];
                    setReportLog((log) => log.map((row, i) => (i === idx ? (updated || row) : row)));
                } catch (e) {
                    window.alert(e?.message || 'Failed to update Issue fields');
                }
            } else {
                setReportLog(log => log.map((row, i) => i === idx ? { ...row, issueChecked: true, issueName: name, issueDate: dateStr } : row));
            }
        }
    }

    function closeNamePrompt() {
        setNamePromptOpen(false);
        setNamePromptKind('');
        setNamePromptIdx(-1);
        setNamePromptTitle('');
        setNamePromptValue('');
        setNamePromptError('');
    }

    async function confirmNamePrompt() {
        const name = String(namePromptValue || '').trim();
        if (!name) {
            setNamePromptError('Name is required.');
            return;
        }
        const kind = String(namePromptKind || '').trim();
        const idx = Number(namePromptIdx);
        closeNamePrompt();
        await applyReportLogName(kind, idx, name);
    }

    function renderNamePromptDialog() {
        if (!namePromptOpen) return null;
        return (
            <Dialog open={namePromptOpen} onClose={closeNamePrompt} maxWidth="xs" fullWidth>
                <DialogTitle>{namePromptTitle || 'Enter name'}</DialogTitle>
                <DialogContent>
                    {namePromptError ? (
                        <Alert severity="error" sx={{ mb: 2 }}>{namePromptError}</Alert>
                    ) : null}
                    <TextField
                        autoFocus
                        fullWidth
                        label="Name"
                        value={namePromptValue}
                        onChange={(e) => setNamePromptValue(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeNamePrompt}>Cancel</Button>
                    <Button variant="contained" onClick={confirmNamePrompt}>Confirm</Button>
                </DialogActions>
            </Dialog>
        );
    }

    function renderChangesModal() {
        if (!changesModal.open) return null;
        return (
            <div
                role="dialog"
                aria-modal="true"
                onClick={() => setChangesModal((p) => ({ ...p, open: false }))}
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.45)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 16
                }}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        width: 'min(980px, 96vw)',
                        maxHeight: 'min(80vh, 720px)',
                        background: 'var(--panel)',
                        color: 'var(--text)',
                        border: '1px solid var(--border)',
                        borderRadius: 12,
                        boxShadow: '0 18px 60px rgba(0,0,0,0.35)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <div
                        style={{
                            padding: '12px 14px',
                            borderBottom: '1px solid var(--border)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10
                        }}
                    >
                        <div style={{ fontWeight: 900, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {changesModal.title || 'Changes'}
                        </div>
                        <button
                            type="button"
                            onClick={() => setChangesModal((p) => ({ ...p, open: false }))}
                            style={{
                                border: '1px solid var(--border)',
                                background: 'var(--input-bg)',
                                color: 'var(--text)',
                                padding: '6px 10px',
                                borderRadius: 10,
                                cursor: 'pointer',
                                fontWeight: 800
                            }}
                        >
                            Close
                        </button>
                    </div>

                    <div style={{ padding: 14, overflow: 'auto' }}>
                        {changesModal.loading ? (
                            <div style={{ color: 'var(--muted)' }}>Loading…</div>
                        ) : changesModal.error ? (
                            <div style={{ color: '#b91c1c', fontWeight: 700 }}>{changesModal.error}</div>
                        ) : !changesModal.items.length ? (
                            <div style={{ color: 'var(--muted)' }}>No audit events were recorded between these two revisions.</div>
                        ) : (
                            <div className="reports-scroll-x">
                                <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--panel)' }}>
                                    <thead>
                                        <tr style={{ background: 'var(--input-bg)' }}>
                                            <th style={{ padding: '10px', border: '1px solid var(--border)' }}>When</th>
                                            <th style={{ padding: '10px', border: '1px solid var(--border)' }}>Action</th>
                                            <th style={{ padding: '10px', border: '1px solid var(--border)' }}>Actor</th>
                                            <th style={{ padding: '10px', border: '1px solid var(--border)' }}>Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {changesModal.items.map((it, i) => {
                                            const when = it?.created_at ? new Date(it.created_at).toLocaleString('en-GB') : '';
                                            const action = String(it?.action || '').trim() || '—';
                                            const who = String(it?.actor_username || '').trim() || '—';
                                            const role = String(it?.actor_role || '').trim();
                                            const meta = (it?.metadata && typeof it.metadata === 'object') ? it.metadata : {};
                                            const details = [];

                                            if (Array.isArray(meta.changed_fields) && meta.changed_fields.length) {
                                                details.push(`fields: ${meta.changed_fields.slice(0, 10).join(', ')}`);
                                            }
                                            if (meta.report_type) details.push(`report: ${meta.report_type}`);
                                            if (meta.report_instance) details.push(`instance: ${meta.report_instance}`);
                                            if (meta.revision != null) details.push(`rev: ${meta.revision}`);
                                            if (meta.notice_id != null) details.push(`notice_id: ${meta.notice_id}`);
                                            if (meta.issue_date) details.push(`issue_date: ${String(meta.issue_date).slice(0, 10)}`);

                                            const detailText = details.length ? details.join(' | ') : '—';
                                            return (
                                                <tr key={it?.id != null ? it.id : i}>
                                                    <td style={{ padding: '10px', border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{when}</td>
                                                    <td style={{ padding: '10px', border: '1px solid var(--border)', fontWeight: 800 }}>{action}</td>
                                                    <td style={{ padding: '10px', border: '1px solid var(--border)' }}>{who}{role ? ` (${role})` : ''}</td>
                                                    <td style={{ padding: '10px', border: '1px solid var(--border)' }}>{detailText}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (!selected) {
        // Project selection list
        const existingList = getSortedFilteredProjects();
        const assignedCount = existingList.filter((p) => String(p?.leadSurveyor || '').trim()).length;
        const questionnairePendingCount = existingList.filter((p) => !p?.questionnaireSentAt && !p?.hasPortalData).length;
        return (
            <Box className={`reports-project-list device-${deviceType}`} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {renderChangesModal()}
                {renderNamePromptDialog()}
                <AnalyticsPageHero
                    eyebrow="Projects"
                    title="Projects and dashboard entry points"
                />

                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }
                    }}
                >
                    <AnalyticsMetricCard
                        icon={<FolderOpenRoundedIcon fontSize="small" />}
                        label="Active Projects"
                        value={existingList.length}
                        caption="Current projects available from the live SQL-backed list view."
                        accent="linear-gradient(135deg, #0f172a, #2563eb)"
                    />
                    <AnalyticsMetricCard
                        icon={<AssignmentTurnedInRoundedIcon fontSize="small" />}
                        label="Portal Access"
                        value={existingList.length}
                        caption="Active projects available in the Project Dashboard and Information Portal."
                        accent="linear-gradient(135deg, #0f766e, #14b8a6)"
                    />
                    <AnalyticsMetricCard
                        icon={<EmailRoundedIcon fontSize="small" />}
                        label="Capture Outstanding"
                        value={questionnairePendingCount}
                        caption="Projects awaiting questionnaire data through the Reporting Period workspace."
                        accent="linear-gradient(135deg, #b45309, #ef4444)"
                    />
                    <AnalyticsMetricCard
                        icon={<AutoAwesomeRoundedIcon fontSize="small" />}
                        label="Assigned Surveyors"
                        value={assignedCount}
                        caption="Projects currently showing a lead surveyor on the list view."
                        accent="linear-gradient(135deg, #4338ca, #7c3aed)"
                    />
                </Box>

                <AnalyticsPanelSection
                    eyebrow="Selection"
                    title="Select a Project"
                    right={(
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                            <TextField
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name or reference..."
                                size="small"
                                sx={{ minWidth: { xs: '100%', sm: 240 } }}
                            />
                            <TextField
                                select
                                size="small"
                                value={sortMode}
                                onChange={(e) => setSortMode(e.target.value)}
                                sx={{ minWidth: 220 }}
                            >
                                <MenuItem value="alpha">Alphabetical</MenuItem>
                                <MenuItem value="reference-desc">Project Reference Descending</MenuItem>
                                <MenuItem value="reference-asc">Project Reference Ascending</MenuItem>
                            </TextField>
                        </Stack>
                    )}
                >
                    {latestReportsError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {latestReportsError}
                        </Alert>
                    )}
                    {existingList.length === 0 && <Typography color="text.secondary">No projects found.</Typography>}
                    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {existingList.map((p) => {
                            const ref = String(p?.reference || '').trim();
                            const isCardExpanded = Boolean(expandedProjectCards?.[ref]);
                            const leadSurveyor = String(p?.leadSurveyor || '').trim();

                            const formatSurveyorShort = (fullName) => {
                                const raw = String(fullName || '').trim();
                                if (!raw) return '';
                                // Handle "Last, First" formats.
                                if (raw.includes(',')) {
                                    const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
                                    if (parts.length >= 2) {
                                        const first = parts[1].split(/\s+/).filter(Boolean)[0] || '';
                                        const last = parts[0].split(/\s+/).filter(Boolean)[0] || '';
                                        if (first && last) return `${first} ${last[0].toUpperCase()}`;
                                    }
                                }
                                const tokens = raw.split(/\s+/).filter(Boolean);
                                if (tokens.length === 1) return tokens[0];
                                const first = tokens[0];
                                const last = tokens[tokens.length - 1];
                                return `${first} ${String(last[0] || '').toUpperCase()}`.trim();
                            };

                            const parseOutstandingItems = (comment) => {
                                const raw = String(comment || '').trim();
                                if (!raw) return [];
                                return raw
                                    .replace(/\s+/g, ' ')
                                    .split(/\s*[•\n]\s*|\s*;\s*|\.(?:\s+|$)/g)
                                    .map((s) => s.trim())
                                    .filter(Boolean)
                                    .slice(0, 12);
                            };

                            const deriveOutstandingTitles = (comment) => {
                                const raw = String(comment || '').trim();
                                if (!raw) return [];

                                const titles = [];
                                const pushUnique = (title) => {
                                    const t = String(title || '').trim();
                                    if (!t) return;
                                    if (!titles.some((x) => x.toLowerCase() === t.toLowerCase())) titles.push(t);
                                };

                                const chunks = raw
                                    .replace(/\s+/g, ' ')
                                    .split(/\s*[•\n]\s*|\s*;\s*|\.(?:\s+|$)/g)
                                    .map((s) => s.trim())
                                    .filter(Boolean)
                                    .slice(0, 24);

                                for (const chunk of chunks) {
                                    const lower = chunk.toLowerCase();

                                    // Ignore generic narrative lines (we don't want these as "documents").
                                    if (
                                        lower.includes('these gaps should be closed') ||
                                        (lower.includes('prior to') && lower.includes('drawdown')) ||
                                        lower.includes('condition of first')
                                    ) {
                                        continue;
                                    }

                                    // Building control initial notice
                                    if (lower.includes('building control') && lower.includes('initial notice')) {
                                        pushUnique('Initial Notice');
                                    }

                                    // Section 106
                                    if (lower.includes('section 106') || lower.includes('s106')) {
                                        pushUnique(lower.includes('executed') ? 'Executed Section 106' : 'Section 106');
                                    }

                                    // S104 / S38 / S278 clarification requests
                                    const sectionMatches = new Set();
                                    const re = /\bS\s*(104|38|278)\b/gi;
                                    let m;
                                    while ((m = re.exec(chunk)) !== null) {
                                        sectionMatches.add(String(m[1]));
                                    }
                                    if (sectionMatches.size) {
                                        // If the text is about progress/approval/status, treat as clarification requests.
                                        const isClarification = lower.includes('progress') || lower.includes('approval') || lower.includes('status') || lower.includes('clarification');
                                        if (isClarification) {
                                            if (sectionMatches.has('104')) pushUnique('Clarification on Section 104 Process');
                                            if (sectionMatches.has('38')) pushUnique('Clarification on Section 38 Process');
                                            if (sectionMatches.has('278')) pushUnique('Clarification on Section 278 Process');
                                        }
                                    }

                                    // Contract / procurement / principal contractor / costs
                                    if (lower.includes('contract particulars') || (lower.includes('signed') && lower.includes('contract'))) {
                                        pushUnique('Signed Contract');
                                    }
                                    if (lower.includes('procurement route')) {
                                        pushUnique('Confirmation of Procurement Route');
                                    }
                                    if (lower.includes('principal contractor') && (lower.includes('appointment') || lower.includes('appoint'))) {
                                        pushUnique('Principal Contractor Appointment');
                                    }
                                    if (lower.includes('cost plan') || (lower.includes('fees') && lower.includes('contingency')) || lower.includes('project costs')) {
                                        pushUnique('Project Costs');
                                    }
                                }

                                return titles;
                            };

                            const humanizeOutstandingItem = (text) => {
                                const t = String(text || '').trim();
                                if (!t) return null;
                                const lower = t.toLowerCase();

                                // Filter out non-document narrative/status lines that can appear in comments.
                                if (
                                    (lower.includes('works start month') && lower.includes('contract particulars')) ||
                                    lower.includes('limiting certainty on commencement') ||
                                    lower.includes('risk controls') ||
                                    lower.includes('these gaps should be closed') ||
                                    (lower.includes('condition') && lower.includes('first drawdown'))
                                ) {
                                    return null;
                                }

                                // Common known items (keep short, title-case, no negative phrasing)
                                if (lower.includes('project cost') || lower.includes('cost information')) return 'Project Cost Information';
                                if (lower.includes('planning decision')) return 'Planning Decision Notice';
                                if (lower.includes('programme') || lower.includes('program')) return 'Project Programme';
                                if (lower.includes('executed contract')) return 'Executed Contract';
                                if (lower.includes('decision notice')) return 'Decision Notice';
                                if (lower.includes('asbestos') && lower.includes('survey')) return 'Asbestos Survey';
                                if (lower.includes('f10') && lower.includes('notif')) return 'F10 Notification';

                                // Insurances (Developer is often referred to as Borrower in reporting)
                                if (lower.includes('consultant') && lower.includes('pi') && lower.includes('insur')) return 'Consultant PI Insurance';
                                if (lower.includes('contractor') && lower.includes('insur')) return 'Contractor Insurance';
                                if (lower.includes('borrower') && lower.includes('insur')) return 'Developer Insurance';
                                if (lower.includes('developer') && lower.includes('insur')) return 'Developer Insurance';
                                if (lower.includes('insur')) return 'Insurance Information';

                                // Generic cleanup: remove negative phrasing.
                                return t
                                    .replace(/\b(not\s+provided|not\s+defined|missing|outstanding)\b/ig, '')
                                    .replace(/\s+/g, ' ')
                                    .trim()
                                    .replace(/^./, (c) => c.toUpperCase());
                            };

                            const phiNode = (() => {
                                const factors = phiFactorsToQuery(phiConfig);
                                const reportInstance = 'initial';
                                const weights = getPhiWeightsForInstance(phiConfig, reportInstance);
                                const weightsSig = weightsToSig(weights);
                                const cfgKey = `${ref}|${reportInstance}|weights:${weightsSig}|${String(phiConfig?.use_ai ? 1 : 0)}|${factors}`;
                                const item = ref ? healthIndexByRef?.[cfgKey] : null;
                                const score = (item && typeof item.score === 'number') ? item.score : (item && Number.isFinite(Number(item.score)) ? Number(item.score) : null);
                                const loading = ref ? Boolean(healthIndexLoadingByRef?.[ref]) : false;
                                const drivers = Array.isArray(item?.drivers) ? item.drivers : [];
                                const scoreLabel = score != null
                                    ? `${Math.max(0, Math.min(100, Math.round(score)))}`
                                    : '—';

                                const displayScore = loading ? '…' : (score != null ? String(Math.max(0, Math.min(100, Math.round(score)))) : '—');
                                const color = (score != null) ? healthIndexColor(score) : 'var(--muted)';

                                const humanizeFlag = (raw) => {
                                    const text = String(raw || '').trim();
                                    if (!text) return null;

                                    const mScore = text.match(/readiness\.score\s*=\s*(\d+)(?:\s*\(([^)]+)\))?/i);
                                    if (mScore) {
                                        const scoreNum = mScore[1];
                                        const status = (mScore[2] || '').replace(/^status\s*/i, '').trim();
                                        return status
                                            ? `Readiness status is ${status} (score ${scoreNum})`
                                            : `Readiness score is ${scoreNum}`;
                                    }

                                    const mMissing = text.match(/readiness\.key_missing\s*:\s*(.+)$/i);
                                    if (mMissing) {
                                        return `Missing key items: ${mMissing[1].trim()}`;
                                    }

                                    const mOutstanding = text.match(/readiness\.outstanding_documents\s*(?:list\s+includes|:)\s*(.+)$/i);
                                    if (mOutstanding) {
                                        return `Outstanding documents: ${mOutstanding[1].trim()}`;
                                    }

                                    return text
                                        .replace(/^\w+\./, '')
                                        .replace(/_/g, ' ')
                                        .replace(/\s+/g, ' ')
                                        .trim();
                                };

                                const actionsFromDrivers = (driverList) => {
                                    const actions = [];

                                    const pushUnique = (action) => {
                                        const a = String(action || '').trim();
                                        if (!a) return;
                                        if (!actions.some((x) => x.toLowerCase() === a.toLowerCase())) actions.push(a);
                                    };

                                    (driverList || []).forEach((raw) => {
                                        const text = String(raw || '').trim();
                                        if (!text) return;

                                        if (/readiness\.key_missing\s*:/i.test(text)) {
                                            const m = text.match(/readiness\.key_missing\s*:\s*(.+)$/i);
                                            const missing = (m?.[1] || '').trim();
                                            if (/costs\s+not\s+defined/i.test(missing)) pushUnique('Define and confirm project costs/budget in the system');
                                            if (/planning\s+decision\s+not\s+defined/i.test(missing)) pushUnique('Record the planning decision status (or mark as not applicable)');
                                            if (!/costs\s+not\s+defined/i.test(missing) && !/planning\s+decision\s+not\s+defined/i.test(missing) && missing) {
                                                pushUnique(`Complete missing key items: ${missing.replace(/\s+/g, ' ').trim()}`);
                                            }
                                            return;
                                        }

                                        if (/readiness\.outstanding_documents/i.test(text)) {
                                            pushUnique('Upload/confirm outstanding core documents (e.g., executed contract, decision notice)');
                                            return;
                                        }

                                        const mScore2 = text.match(/readiness\.score\s*=\s*(\d+)/i);
                                        if (mScore2) {
                                            const scoreNum = Number(mScore2[1]);
                                            if (!Number.isNaN(scoreNum) && scoreNum <= 0) {
                                                pushUnique('Resolve the key readiness blockers to move status from red');
                                            }
                                            return;
                                        }

                                        if (/readiness\./i.test(text)) {
                                            pushUnique('Review readiness data and resolve any flagged issues');
                                        }
                                    });

                                    if (!actions.length) {
                                        pushUnique('Review the project data and address any outstanding items to improve the score');
                                    }

                                    return actions.slice(0, 5);
                                };

                                const flags = drivers
                                    .slice(0, 5)
                                    .map(humanizeFlag)
                                    .filter(Boolean);
                                const actionsRequired = actionsFromDrivers(drivers);
                                const tooltipTitle = (
                                    <Box sx={{ maxWidth: isPhone ? 'min(320px, 86vw)' : 420 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>
                                            Project Health Index: {scoreLabel}
                                        </Typography>

                                        <Typography variant="overline" sx={{ display: 'block', opacity: 0.9, mt: 0.5 }}>
                                            Flags
                                        </Typography>
                                        {flags.length ? (
                                            <Box component="ul" sx={{ mt: 0.25, mb: 0.5, pl: 2.25 }}>
                                                {flags.map((f) => (
                                                    <Typography key={f} component="li" variant="caption" sx={{ opacity: 0.95, mb: 0.25 }}>
                                                        {f}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
                                                None
                                            </Typography>
                                        )}

                                        <Typography variant="overline" sx={{ display: 'block', opacity: 0.9, mt: 0.25 }}>
                                            Actions Required
                                        </Typography>
                                        {actionsRequired.length ? (
                                            <Box component="ul" sx={{ mt: 0.25, mb: 0, pl: 2.25 }}>
                                                {actionsRequired.map((a) => (
                                                    <Typography key={a} component="li" variant="caption" sx={{ opacity: 0.95, mb: 0.25 }}>
                                                        {a}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
                                                —
                                            </Typography>
                                        )}
                                    </Box>
                                );

                                return (
                                    <Tooltip
                                        title={tooltipTitle}
                                        arrow
                                        placement="bottom-start"
                                        enterDelay={150}
                                        enterNextDelay={100}
                                        PopperProps={{
                                            strategy: 'fixed',
                                            modifiers: [
                                                { name: 'offset', options: { offset: [0, 8] } },
                                                { name: 'preventOverflow', options: { padding: 8, boundary: 'viewport' } },
                                                { name: 'flip', options: { padding: 8 } },
                                                { name: 'computeStyles', options: { adaptive: false } }
                                            ]
                                        }}
                                        componentsProps={{
                                            tooltip: {
                                                sx: {
                                                    fontSize: 12,
                                                    lineHeight: 1.25,
                                                    bgcolor: 'rgba(17, 24, 39, 0.96)',
                                                    p: 1.25,
                                                    borderRadius: 2,
                                                    boxShadow: '0 10px 25px rgba(0,0,0,0.18)',
                                                    maxWidth: isPhone ? 'min(320px, 86vw)' : 420,
                                                    overflowWrap: 'anywhere',
                                                    wordBreak: 'break-word'
                                                }
                                            },
                                            arrow: {
                                                sx: {
                                                    color: 'rgba(17, 24, 39, 0.96)'
                                                }
                                            }
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontWeight: 800,
                                                color,
                                                padding: '2px 8px',
                                                borderRadius: 999,
                                                border: `1px solid ${score != null ? color : '#d1d5db'}`,
                                                background: '#fff',
                                                whiteSpace: 'nowrap',
                                                cursor: 'help'
                                            }}
                                        >
                                            {(isPhone ? 'PHI' : 'Project Health Index')}: {displayScore}
                                        </span>
                                    </Tooltip>
                                );
                            })();

                            const outItem = ref ? outstandingInfoByRef?.[ref] : null;
                            const outLoading = ref ? Boolean(outstandingInfoLoadingByRef?.[ref]) : false;
                            const outRating = String(outItem?.rating || '').trim();
                            // Always use orange for the projects-page Information Outstanding pill.
                            const outColor = '#d97706';
                            const derivedOutstanding = deriveOutstandingTitles(outItem?.comment);
                            const outItems = (derivedOutstanding && derivedOutstanding.length ? derivedOutstanding : (parseOutstandingItems(outItem?.comment) || []))
                                .map(humanizeOutstandingItem)
                                .filter(Boolean);
                            const outCount = outLoading ? null : outItems.length;
                            const outLabel = outLoading ? '…' : String(outCount ?? 0);
                            const outTooltipTitle = (
                                <Box sx={{ maxWidth: 420 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>
                                        Outstanding Information
                                    </Typography>
                                    {outItems.length ? (
                                        <Box component="ul" sx={{ mt: 0.25, mb: 0, pl: 2.25 }}>
                                            {outItems.slice(0, 10).map((it) => (
                                                <Typography key={it} component="li" variant="caption" sx={{ opacity: 0.95, mb: 0.25 }}>
                                                    {it}
                                                </Typography>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
                                            None
                                        </Typography>
                                    )}
                                </Box>
                            );
                            const outPill = (
                                <Tooltip
                                    title={outTooltipTitle}
                                    arrow
                                    placement="bottom-start"
                                    disableHoverListener={outLoading}
                                    PopperProps={{
                                        strategy: 'fixed',
                                        modifiers: [
                                            { name: 'offset', options: { offset: [0, 8] } },
                                            { name: 'preventOverflow', options: { padding: 8, boundary: 'viewport' } },
                                            { name: 'flip', options: { padding: 8 } },
                                            { name: 'computeStyles', options: { adaptive: false } }
                                        ]
                                    }}
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                fontSize: 12,
                                                lineHeight: 1.25,
                                                bgcolor: 'rgba(17, 24, 39, 0.96)',
                                                p: 1.0,
                                                borderRadius: 2,
                                                boxShadow: '0 10px 25px rgba(0,0,0,0.18)',
                                                maxWidth: isPhone ? 'min(320px, 86vw)' : 420,
                                                overflowWrap: 'anywhere',
                                                wordBreak: 'break-word'
                                            }
                                        },
                                        arrow: {
                                            sx: { color: 'rgba(17, 24, 39, 0.96)' }
                                        }
                                    }}
                                >
                                    <span
                                        style={{
                                            fontWeight: 800,
                                            color: outColor,
                                            padding: '2px 8px',
                                            borderRadius: 999,
                                            border: `1px solid ${outRating ? outColor : '#d1d5db'}`,
                                            background: '#fff',
                                            whiteSpace: 'nowrap',
                                            cursor: outLoading ? 'default' : 'help'
                                        }}
                                    >
                                        {(isPhone ? 'Info Outstanding' : 'Information Outstanding')}: {outLabel}
                                    </span>
                                </Tooltip>
                            );

                            const canShowPortalIndicators = Boolean(p?.questionnaireSentAt) || Boolean(p?.hasPortalData);
                            const refUpper = String(ref || '').toUpperCase();
                            const hasProjectSetupDone = ref ? Boolean(projectSetupByRef?.[refUpper]?.completedAt) : false;
                            const initialReportIssued = Boolean(latestReportsByRef?.[ref]?.initial);
                            const requiresProjectSetup = initialReportIssued && !hasProjectSetupDone;
                            const readinessLabel = canShowPortalIndicators
                                ? 'Dashboard ready'
                                : requiresProjectSetup
                                    ? 'Live Confirmation Required'
                                    : 'Portal ready';
                            const reportBadges = getProjectReportBadges({
                                reportState: latestReportsByRef?.[ref],
                                initialConfirmedLive: hasProjectSetupDone,
                                inProgressReportType: p?.inProgressReportType,
                                inProgressInterimNumber: p?.inProgressInterimNumber
                            });

                            return (
                                <Box component="li" key={p.id || p.reference} sx={{ m: 0 }}>
                                    <Paper
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => toggleProjectCard(ref)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                toggleProjectCard(ref);
                                            }
                                        }}
                                        sx={{
                                            ...analyticsPanelSx,
                                            position: 'relative',
                                            overflow: 'hidden',
                                            p: { xs: 2, sm: 2.5 },
                                            cursor: 'pointer',
                                            background: canShowPortalIndicators
                                                ? `linear-gradient(180deg, ${alpha('#ffffff', 0.95)} 0%, ${alpha('#f8fbff', 0.9)} 100%)`
                                                : `linear-gradient(180deg, ${alpha('#fffaf5', 0.96)} 0%, ${alpha('#ffffff', 0.92)} 100%)`,
                                            boxShadow: canShowPortalIndicators
                                                ? '0 24px 54px rgba(15, 23, 42, 0.10)'
                                                : '0 20px 40px rgba(180, 83, 9, 0.08)',
                                            transition: 'box-shadow 220ms ease',
                                            '&:hover': {
                                                boxShadow: canShowPortalIndicators
                                                    ? '0 28px 62px rgba(15, 23, 42, 0.14)'
                                                    : '0 24px 46px rgba(180, 83, 9, 0.12)'
                                            },
                                            '&:focus-visible': {
                                                outline: `2px solid ${alpha('#2563eb', 0.55)}`,
                                                outlineOffset: 2
                                            },
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                inset: '0 auto 0 0',
                                                width: 6,
                                                background: canShowPortalIndicators
                                                    ? 'linear-gradient(180deg, #2563eb 0%, #0f766e 100%)'
                                                    : 'linear-gradient(180deg, #f59e0b 0%, #b45309 100%)'
                                            }
                                        }}
                                    >
                                        <Box sx={{ pl: { xs: 0.5, sm: 0.75 } }}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                                    justifyContent: 'space-between',
                                                    gap: 1.2,
                                                    mb: isCardExpanded ? 1.6 : 0
                                                }}
                                            >
                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            minWidth: 0
                                                        }}
                                                    >
                                                        <Chip
                                                            label={ref || '—'}
                                                            sx={{
                                                                fontWeight: 800,
                                                                alignSelf: 'flex-start',
                                                                backgroundColor: alpha('#0f172a', 0.06)
                                                            }}
                                                        />
                                                        <Chip
                                                            label={readinessLabel}
                                                            sx={{
                                                                fontWeight: 700,
                                                                alignSelf: 'flex-start',
                                                                color: canShowPortalIndicators ? '#065f46' : '#9a3412',
                                                                backgroundColor: canShowPortalIndicators ? alpha('#10b981', 0.16) : alpha('#f59e0b', 0.16),
                                                                border: `1px solid ${canShowPortalIndicators ? alpha('#10b981', 0.24) : alpha('#f59e0b', 0.24)}`
                                                            }}
                                                        />
                                                        {reportBadges.map((badgeLabel, badgeIndex) => (
                                                            <Chip
                                                                key={`${ref}-report-badge-${badgeLabel}`}
                                                                label={badgeLabel}
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    alignSelf: 'flex-start',
                                                                    color: badgeIndex === 0 ? '#1e3a8a' : '#9a3412',
                                                                    backgroundColor: badgeIndex === 0 ? alpha('#2563eb', 0.1) : alpha('#f59e0b', 0.12),
                                                                    border: `1px solid ${badgeIndex === 0 ? alpha('#2563eb', 0.18) : alpha('#f59e0b', 0.2)}`
                                                                }}
                                                            />
                                                        ))}

                                                        <Typography
                                                            sx={{
                                                                flex: '0 1 auto',
                                                                minWidth: 0,
                                                                maxWidth: isCardExpanded ? 0 : { xs: '100%', sm: 'min(360px, 42vw)' },
                                                                ml: isCardExpanded ? 0 : 0.15,
                                                                pr: isCardExpanded ? 0 : 1,
                                                                opacity: isCardExpanded ? 0 : 1,
                                                                transform: isCardExpanded ? 'translateX(-10px)' : 'translateX(0)',
                                                                transition: 'opacity 220ms ease, transform 220ms ease, max-width 240ms ease, margin 220ms ease, padding 220ms ease',
                                                                overflow: 'hidden',
                                                                whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                                                textOverflow: 'ellipsis',
                                                                fontWeight: 900,
                                                                fontSize: { xs: '1.08rem', sm: '1.2rem' },
                                                                letterSpacing: '-0.03em',
                                                                lineHeight: 1.15,
                                                                overflowWrap: 'anywhere'
                                                            }}
                                                        >
                                                            {p.name || 'Untitled Project'}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box
                                                    aria-hidden="true"
                                                    sx={{
                                                        mt: { xs: 0.25, sm: 0 },
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: '50%',
                                                        display: 'grid',
                                                        placeItems: 'center',
                                                        backgroundColor: alpha('#ffffff', 0.78),
                                                        border: `1px solid ${alpha('#0f172a', 0.08)}`,
                                                        color: '#334155',
                                                        transform: isCardExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: 'transform 240ms ease, background-color 240ms ease'
                                                    }}
                                                >
                                                    <ExpandMoreRoundedIcon fontSize="small" />
                                                </Box>
                                            </Box>

                                            <Collapse in={isCardExpanded} timeout={280} unmountOnExit>
                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: isPhone ? '1fr' : 'minmax(320px, 1.2fr) minmax(250px, 0.9fr) auto',
                                                        gap: 1.75,
                                                        alignItems: 'stretch'
                                                    }}
                                                >
                                                    {/* Left: project details */}
                                                    <Box
                                                        sx={{
                                                            minWidth: 0,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 1.25,
                                                            p: { xs: 1.4, sm: 1.6 },
                                                            borderRadius: 3,
                                                            backgroundColor: alpha('#ffffff', 0.58),
                                                            border: `1px solid ${alpha('#0f172a', 0.06)}`
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                                                            <div style={{ minWidth: 0 }}>
                                                                <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.45 }}>
                                                                    Project Overview
                                                                </Typography>
                                                                <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: '-0.03em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {p.name || 'Untitled Project'}
                                                                </div>
                                                                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.35, overflowWrap: 'anywhere' }}>
                                                                    {String(p?.siteAddress || '').trim() || 'No site address recorded'}
                                                                </Typography>
                                                            </div>

                                                            {isPhone && canShowPortalIndicators ? (
                                                                <IconButton
                                                                    size="small"
                                                                    aria-label="Project actions"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        setProjectActionsMenu({ anchorEl: e.currentTarget, project: p });
                                                                    }}
                                                                >
                                                                    <MoreVertIcon fontSize="small" />
                                                                </IconButton>
                                                            ) : null}
                                                        </div>
                                                        <Box
                                                            sx={{
                                                                display: 'grid',
                                                                gap: 1,
                                                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }
                                                            }}
                                                        >
                                                            <Box sx={{ p: 1.1, borderRadius: 2.5, backgroundColor: alpha('#f8fbff', 0.9), border: `1px solid ${alpha('#2563eb', 0.08)}` }}>
                                                                <Typography sx={{ color: 'text.secondary', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                                                    Lead Surveyor
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ mt: 0.45, fontWeight: 700 }}>
                                                                    {leadSurveyor ? (isPhone ? formatSurveyorShort(leadSurveyor) : leadSurveyor) : 'Unassigned'}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ p: 1.1, borderRadius: 2.5, backgroundColor: alpha('#f8fbff', 0.9), border: `1px solid ${alpha('#2563eb', 0.08)}` }}>
                                                                <Typography sx={{ color: 'text.secondary', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                                                    Funder
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ mt: 0.45, fontWeight: 700, overflowWrap: 'anywhere' }}>
                                                                    {String(p?.funder || '').trim() || 'Not recorded'}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        {latestReportsLoading && (
                                                            <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>Checking latest reports…</Typography>
                                                        )}
                                                    </Box>

                                                    {/* Middle: statuses */}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 1.1,
                                                            justifyContent: 'center',
                                                            p: { xs: 1.4, sm: 1.6 },
                                                            borderRadius: 3,
                                                            backgroundColor: alpha('#ffffff', 0.58),
                                                            border: `1px solid ${alpha('#0f172a', 0.06)}`
                                                        }}
                                                    >
                                                        <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                                            Delivery Signals
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                            Readiness and information gap indicators remain tied to the same live calculations.
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: isPhone ? 'row' : 'column',
                                                                flexWrap: isPhone ? 'wrap' : 'nowrap',
                                                                gap: 1,
                                                                alignItems: isPhone ? 'flex-start' : 'stretch',
                                                                justifyContent: 'center'
                                                            }}
                                                        >
                                                            {phiNode}
                                                            {outPill}
                                                            {isPhone ? (
                                                                <>
                                                                    <Button
                                                                        variant="contained"
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            setSelected(p);
                                                                        }}
                                                                    >
                                                                        Project Dashboard
                                                                    </Button>
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            openInformationDashboard(p.reference);
                                                                        }}
                                                                    >
                                                                        Information Portal
                                                                    </Button>
                                                                </>
                                                            ) : null}
                                                        </Box>
                                                    </Box>

                                                    {/* Right: actions */}
                                                    {!isPhone ? (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'flex-end'
                                                            }}
                                                        >
                                                            <Stack
                                                                direction="column"
                                                                spacing={1}
                                                                sx={{
                                                                    alignItems: 'stretch',
                                                                    minWidth: 190,
                                                                    p: 1.4,
                                                                    borderRadius: 3,
                                                                    backgroundColor: alpha('#ffffff', 0.58),
                                                                    border: `1px solid ${alpha('#0f172a', 0.06)}`
                                                                }}
                                                            >
                                                                <Typography sx={{ color: 'text.secondary', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                                                    Actions
                                                                </Typography>
                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openSnapshotSummary(p.reference); }}
                                                                    sx={{ bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' }, justifyContent: 'space-between' }}
                                                                >
                                                                    AI Summary
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    sx={{ justifyContent: 'space-between' }}
                                                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSelected(p); }}
                                                                >
                                                                    Project Dashboard
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    sx={{ justifyContent: 'space-between' }}
                                                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openInformationDashboard(p.reference); }}
                                                                >
                                                                    Information Portal
                                                                </Button>
                                                            </Stack>
                                                        </Box>
                                                    ) : null}
                                                </Box>
                                            </Collapse>
                                        </Box>
                                    </Paper>
                                </Box>
                            );
                        })}
                    </Box>
                </AnalyticsPanelSection>

                <Dialog
                    open={Boolean(projectSetupModal?.open)}
                    onClose={closeProjectSetupModal}
                    fullWidth
                    maxWidth="md"
                >
                    <DialogTitle>
                        {projectSetupModal?.step === 1 ? 'Confirm Live: Programme' : 'Confirm Live: Development Costs'}
                    </DialogTitle>
                    <DialogContent>
                        {projectSetupModal?.step === 1 ? (
                            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                                <Typography sx={{ color: 'text.secondary' }}>
                                    Confirm the live programme baseline. Interim questionnaires will use this structured tracker before OCR fallback.
                                </Typography>
                                <TextField
                                    label="Works Start Date (MM/YYYY)"
                                    type="month"
                                    value={projectSetupModal?.worksStartMonth || ''}
                                    onChange={(e) => setProjectSetupModal((prev) => ({ ...prev, worksStartMonth: e.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />
                                <TextField
                                    label="Current Borrower Duration Estimate (months)"
                                    type="number"
                                    inputProps={{ min: 1, step: 1 }}
                                    value={projectSetupModal?.borrowerDurationMonths || ''}
                                    onChange={(e) => setProjectSetupModal((prev) => ({ ...prev, borrowerDurationMonths: e.target.value }))}
                                    fullWidth
                                />
                                <TextField
                                    label="Current Surveyor Duration Estimate (months)"
                                    type="number"
                                    inputProps={{ min: 1, step: 1 }}
                                    value={projectSetupModal?.surveyorDurationMonths || ''}
                                    onChange={(e) => setProjectSetupModal((prev) => ({ ...prev, surveyorDurationMonths: e.target.value }))}
                                    fullWidth
                                />

                                <Box sx={{ border: `1px solid ${alpha('#0f172a', 0.14)}`, borderRadius: 2, p: 1.2, bgcolor: alpha('#f8fbff', 0.85) }}>
                                    <Typography sx={{ fontWeight: 800, mb: 0.8 }}>Calculated Completion Dates</Typography>
                                    <Typography variant="body2" sx={{ mb: 0.4 }}>
                                        Borrower completion: {addMonthsToMonthYear(projectSetupModal?.worksStartMonth, projectSetupModal?.borrowerDurationMonths)}
                                    </Typography>
                                    <Typography variant="body2">
                                        Surveyor completion: {addMonthsToMonthYear(projectSetupModal?.worksStartMonth, projectSetupModal?.surveyorDurationMonths)}
                                    </Typography>
                                </Box>
                            </Stack>
                        ) : (
                            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                                <Typography sx={{ color: 'text.secondary' }}>
                                    Confirm development cost budgets and opening balances. These values feed editable cost tables and drawdown history. Professional Team confirmation will be added as the next structured domain.
                                </Typography>

                                {(() => {
                                    const sectionConfig = [
                                        { key: 'constructionBudgetRows', title: 'Construction Budgets', summaryLabel: 'Construction Costs' },
                                        { key: 'contingencyBudgetRows', title: 'Contingency Budgets', summaryLabel: 'Contingency' },
                                        { key: 'professionalFeeBudgetRows', title: 'Professional Fee Budgets', summaryLabel: 'Professional Fees' },
                                        { key: 'additionalCostBudgetRows', title: 'Additional Cost Budgets', summaryLabel: 'Additional Costs' }
                                    ];

                                    const sectionTotals = sectionConfig.map((section) => {
                                        const rows = Array.isArray(projectSetupModal?.[section.key]) ? projectSetupModal[section.key] : [];
                                        const isContingency = section.key === 'contingencyBudgetRows';
                                        const budget = isContingency
                                            ? toMoneyNumber(projectSetupModal?.contingencyBudgetTotal)
                                            : sumSectionBudget(rows);
                                        const costToDate = sumSectionCostToDate(rows);
                                        const remaining = budget - costToDate;
                                        const percentComplete = budget > 0 ? `${Math.max(0, Math.min(100, Math.round((costToDate / budget) * 100)))}%` : '0%';
                                        return { ...section, rows, budget, costToDate, remaining, percentComplete };
                                    });

                                    const totalBudget = sectionTotals.reduce((sum, section) => sum + section.budget, 0);
                                    const totalCostToDate = sectionTotals.reduce((sum, section) => sum + section.costToDate, 0);
                                    const totalRemaining = totalBudget - totalCostToDate;
                                    const totalPercentComplete = totalBudget > 0
                                        ? `${Math.max(0, Math.min(100, Math.round((totalCostToDate / totalBudget) * 100)))}%`
                                        : '0%';

                                    const drawdownRows = Array.isArray(projectSetupModal?.previousDrawdownRows) ? projectSetupModal.previousDrawdownRows : [];
                                    const drawdownTotal = drawdownRows.reduce((sum, row) => sum + toMoneyNumber(row?.amount), 0);
                                    const borrowerInputTotal = projectSetupModal?.borrowerInputRelevant ? toMoneyNumber(projectSetupModal?.borrowerInputAmount) : 0;
                                    const drawdownAndBorrowerInputTotal = drawdownTotal + borrowerInputTotal;
                                    const drawdownDifference = totalCostToDate - drawdownAndBorrowerInputTotal;
                                    const isDrawdownBalanced = Math.abs(drawdownDifference) <= 0.009;

                                    return (
                                        <>
                                            <Box sx={{ border: `1px solid ${alpha('#0f172a', 0.14)}`, borderRadius: 2, p: 1.2, bgcolor: alpha('#f8fbff', 0.85) }}>
                                                <Typography sx={{ fontWeight: 800, mb: 0.8 }}>Budget Status</Typography>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                    <thead>
                                                        <tr style={{ background: '#f3f4f6' }}>
                                                            <th style={{ textAlign: 'left', border: '1px solid #e5e7eb', padding: '6px 8px' }}>Description</th>
                                                            <th style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px' }}>Borrower Assessment (Forecast)</th>
                                                            <th style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px' }}>% Complete</th>
                                                            <th style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px' }}>Cost to Date</th>
                                                            <th style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px' }}>Budget Remaining</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {sectionTotals.map((section) => (
                                                            <tr key={`setup-summary-${section.key}`}>
                                                                <td style={{ border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 700 }}>{section.summaryLabel}</td>
                                                                <td style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 700 }}>{formatPounds(section.budget)}</td>
                                                                <td style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 700 }}>{section.percentComplete}</td>
                                                                <td style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 700 }}>{formatPounds(section.costToDate)}</td>
                                                                <td style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 700 }}>{formatPounds(section.remaining)}</td>
                                                            </tr>
                                                        ))}
                                                        <tr style={{ background: '#f3f4f6' }}>
                                                            <td style={{ border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 900 }}>Total Development Cost</td>
                                                            <td style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 900 }}>{formatPounds(totalBudget)}</td>
                                                            <td style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 900 }}>{totalPercentComplete}</td>
                                                            <td style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 900 }}>{formatPounds(totalCostToDate)}</td>
                                                            <td style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 900 }}>{formatPounds(totalRemaining)}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </Box>

                                            {sectionTotals.map((section) => (
                                                <Box key={`setup-section-${section.key}`} sx={{ border: `1px solid ${alpha('#0f172a', 0.2)}`, borderRadius: 2, p: 1.2, bgcolor: '#f8fafc' }}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                                                        <Typography sx={{ fontWeight: 800 }}>{section.title}</Typography>
                                                        <Button size="small" variant="outlined" onClick={() => addSetupSectionRow(section.key)}>Add line</Button>
                                                    </Stack>
                                                    {section.key === 'contingencyBudgetRows' ? (
                                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.1} sx={{ mb: 1.1 }} alignItems={{ sm: 'center' }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 220 }}>Set Total Contingency Budget</Typography>
                                                            <TextField
                                                                value={projectSetupModal?.contingencyBudgetTotal || ''}
                                                                onChange={(e) => setProjectSetupModal((prev) => ({ ...prev, contingencyBudgetTotal: e.target.value }))}
                                                                type="number"
                                                                inputProps={{ min: 0, step: '0.01' }}
                                                                InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                                                                size="small"
                                                                sx={{ maxWidth: 260 }}
                                                            />
                                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                Use rows below to record contingency claim descriptions and values.
                                                            </Typography>
                                                        </Stack>
                                                    ) : null}
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#ffffff' }}>
                                                        <thead>
                                                            <tr style={{ background: '#f3f4f6' }}>
                                                                <th style={{ textAlign: 'left', border: '1px solid #e5e7eb', padding: '6px 8px' }}>{section.key === 'contingencyBudgetRows' ? 'Contingency Claim' : 'Budget Item'}</th>
                                                                {section.key === 'contingencyBudgetRows' ? null : (
                                                                    <th style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px' }}>Budget</th>
                                                                )}
                                                                <th style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px' }}>{section.key === 'contingencyBudgetRows' ? 'Claim Amount' : 'Cost to Date'}</th>
                                                                <th style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px' }}>{section.key === 'contingencyBudgetRows' ? 'Contingency Remaining' : 'Remaining Budget'}</th>
                                                                <th style={{ textAlign: 'center', border: '1px solid #e5e7eb', padding: '6px 8px', width: 84 }}>Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {section.rows.map((row, idx) => {
                                                                const budgetValue = toMoneyNumber(row?.budget);
                                                                const costToDateValue = toMoneyNumber(row?.costToDate);
                                                                const rowRemaining = section.key === 'contingencyBudgetRows'
                                                                    ? (section.budget - section.costToDate)
                                                                    : (budgetValue - costToDateValue);
                                                                return (
                                                                    <tr key={`setup-${section.key}-${idx}`}>
                                                                        <td style={{ border: '1px solid #e5e7eb', padding: '6px 8px' }}>
                                                                            <TextField
                                                                                value={row?.item || ''}
                                                                                onChange={(e) => updateSetupSectionRow(section.key, idx, 'item', e.target.value)}
                                                                                size="small"
                                                                                fullWidth
                                                                            />
                                                                        </td>
                                                                        {section.key === 'contingencyBudgetRows' ? null : (
                                                                            <td style={{ border: '1px solid #e5e7eb', padding: '6px 8px' }}>
                                                                                <TextField
                                                                                    value={row?.budget || ''}
                                                                                    onChange={(e) => updateSetupSectionRow(section.key, idx, 'budget', e.target.value)}
                                                                                    type="number"
                                                                                    inputProps={{ min: 0, step: '0.01' }}
                                                                                    InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                                                                                    size="small"
                                                                                    fullWidth
                                                                                />
                                                                            </td>
                                                                        )}
                                                                        <td style={{ border: '1px solid #e5e7eb', padding: '6px 8px' }}>
                                                                            <TextField
                                                                                value={row?.costToDate || ''}
                                                                                onChange={(e) => updateSetupSectionRow(section.key, idx, 'costToDate', e.target.value)}
                                                                                type="number"
                                                                                inputProps={{ min: 0, step: '0.01' }}
                                                                                InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                                                                                size="small"
                                                                                fullWidth
                                                                            />
                                                                        </td>
                                                                        <td style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 700 }}>{formatPounds(rowRemaining)}</td>
                                                                        <td style={{ textAlign: 'center', border: '1px solid #e5e7eb', padding: '6px 8px' }}>
                                                                            <Button size="small" color="error" onClick={() => removeSetupSectionRow(section.key, idx)}>Remove</Button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                            <tr style={{ background: '#f8fafc' }}>
                                                                <td style={{ border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 800 }}>Total</td>
                                                                {section.key === 'contingencyBudgetRows' ? null : (
                                                                    <td style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 800 }}>{formatPounds(section.budget)}</td>
                                                                )}
                                                                <td style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 800 }}>{formatPounds(section.costToDate)}</td>
                                                                <td style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 800 }}>{formatPounds(section.remaining)}</td>
                                                                <td style={{ border: '1px solid #e5e7eb', padding: '6px 8px' }} />
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </Box>
                                            ))}

                                            <Box sx={{ border: `1px solid ${alpha('#0f172a', 0.2)}`, borderRadius: 2, p: 1.2, bgcolor: '#f8fafc' }}>
                                                <Typography sx={{ fontWeight: 800, mb: 0.8 }}>Borrower Input</Typography>
                                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                                                    <Typography variant="body2" sx={{ minWidth: 220, fontWeight: 700 }}>Is Borrower input relevant?</Typography>
                                                    <Stack direction="row" spacing={1}>
                                                        <FormControlLabel
                                                            control={<Checkbox checked={Boolean(projectSetupModal?.borrowerInputRelevant)} onChange={() => setProjectSetupModal((prev) => ({ ...prev, borrowerInputRelevant: true }))} />}
                                                            label="Yes"
                                                        />
                                                        <FormControlLabel
                                                            control={<Checkbox checked={!Boolean(projectSetupModal?.borrowerInputRelevant)} onChange={() => setProjectSetupModal((prev) => ({ ...prev, borrowerInputRelevant: false, borrowerInputAmount: '' }))} />}
                                                            label="No"
                                                        />
                                                    </Stack>
                                                    {projectSetupModal?.borrowerInputRelevant ? (
                                                        <TextField
                                                            label="Borrower Input Amount"
                                                            value={projectSetupModal?.borrowerInputAmount || ''}
                                                            onChange={(e) => setProjectSetupModal((prev) => ({ ...prev, borrowerInputAmount: e.target.value }))}
                                                            type="number"
                                                            inputProps={{ min: 0, step: '0.01' }}
                                                            InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                                                            size="small"
                                                            sx={{ maxWidth: 280 }}
                                                        />
                                                    ) : null}
                                                </Stack>
                                            </Box>

                                            <Box sx={{ border: `1px solid ${alpha('#0f172a', 0.2)}`, borderRadius: 2, p: 1.2, bgcolor: '#f8fafc' }}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                                                    <Typography sx={{ fontWeight: 800 }}>Previous Drawdowns</Typography>
                                                    <Button size="small" variant="outlined" onClick={addSetupDrawdownRow}>Add line</Button>
                                                </Stack>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#ffffff' }}>
                                                    <thead>
                                                        <tr style={{ background: '#f3f4f6' }}>
                                                            <th style={{ textAlign: 'left', border: '1px solid #e5e7eb', padding: '6px 8px' }}>Drawdown</th>
                                                            <th style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px' }}>Amount To Date</th>
                                                            <th style={{ textAlign: 'center', border: '1px solid #e5e7eb', padding: '6px 8px', width: 84 }}>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {drawdownRows.map((row, idx) => (
                                                            <tr key={`setup-drawdown-${idx}`}>
                                                                <td style={{ border: '1px solid #e5e7eb', padding: '6px 8px' }}>
                                                                    <TextField
                                                                        value={row?.item || ''}
                                                                        onChange={(e) => updateSetupDrawdownRow(idx, 'item', e.target.value)}
                                                                        size="small"
                                                                        fullWidth
                                                                    />
                                                                </td>
                                                                <td style={{ border: '1px solid #e5e7eb', padding: '6px 8px' }}>
                                                                    <TextField
                                                                        value={row?.amount || ''}
                                                                        onChange={(e) => updateSetupDrawdownRow(idx, 'amount', e.target.value)}
                                                                        type="number"
                                                                        inputProps={{ min: 0, step: '0.01' }}
                                                                        size="small"
                                                                        fullWidth
                                                                    />
                                                                </td>
                                                                <td style={{ textAlign: 'center', border: '1px solid #e5e7eb', padding: '6px 8px' }}>
                                                                    <Button size="small" color="error" onClick={() => removeSetupDrawdownRow(idx)}>Remove</Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        <tr style={{ background: '#f8fafc' }}>
                                                            <td style={{ border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 800 }}>Total Previous Drawdowns</td>
                                                            <td style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 800 }}>{formatPounds(drawdownTotal)}</td>
                                                            <td style={{ border: '1px solid #e5e7eb', padding: '6px 8px' }} />
                                                        </tr>
                                                        {projectSetupModal?.borrowerInputRelevant ? (
                                                            <tr style={{ background: '#f8fafc' }}>
                                                                <td style={{ border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 800 }}>Borrower Input</td>
                                                                <td style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 800 }}>{formatPounds(borrowerInputTotal)}</td>
                                                                <td style={{ border: '1px solid #e5e7eb', padding: '6px 8px' }} />
                                                            </tr>
                                                        ) : null}
                                                        <tr style={{ background: '#f3f4f6' }}>
                                                            <td style={{ border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 900 }}>Total Drawdowns + Borrower Input</td>
                                                            <td style={{ textAlign: 'right', border: '1px solid #e5e7eb', padding: '6px 8px', fontWeight: 900 }}>{formatPounds(drawdownAndBorrowerInputTotal)}</td>
                                                            <td style={{ border: '1px solid #e5e7eb', padding: '6px 8px' }} />
                                                        </tr>
                                                    </tbody>
                                                </table>

                                                <Typography
                                                    variant="body2"
                                                    sx={{ mt: 1, color: isDrawdownBalanced ? 'success.main' : 'error.main', fontWeight: 700 }}
                                                >
                                                    {isDrawdownBalanced
                                                        ? 'Drawdown total plus Borrower Input matches combined Cost to Date totals.'
                                                        : `Drawdowns plus Borrower Input must match combined Cost to Date totals. Difference: ${formatPounds(drawdownDifference)}`}
                                                </Typography>
                                            </Box>
                                        </>
                                    );
                                })()}
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={closeProjectSetupModal}>Cancel</Button>
                        {projectSetupModal?.step === 2 ? (
                            <Button variant="outlined" onClick={() => setProjectSetupModal((prev) => ({ ...prev, step: 1 }))}>Back</Button>
                        ) : null}
                        {projectSetupModal?.step === 1 ? (
                            <Button variant="contained" onClick={submitProjectSetupStepOne}>Next</Button>
                        ) : (
                            <Button variant="contained" onClick={() => void saveProjectSetupModal()} disabled={projectSetupSaving}>
                                {projectSetupSaving ? 'Confirming…' : 'Confirm Project Live'}
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>

                {(() => {
                    const proj = projectActionsMenu?.project;
                    const eligible = Boolean(proj?.questionnaireSentAt) || Boolean(proj?.hasPortalData);
                    return (
                        <Menu
                            anchorEl={projectActionsMenu?.anchorEl}
                            open={Boolean(projectActionsMenu?.anchorEl) && eligible}
                            onClose={() => setProjectActionsMenu({ anchorEl: null, project: null })}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <MenuItem
                                onClick={() => {
                                    const proj = projectActionsMenu?.project;
                                    setProjectActionsMenu({ anchorEl: null, project: null });
                                    if (proj?.reference) openSnapshotSummary(proj.reference);
                                }}
                            >
                                AI Summary
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    const proj = projectActionsMenu?.project;
                                    setProjectActionsMenu({ anchorEl: null, project: null });
                                    if (proj) setSelected(proj);
                                }}
                            >
                                Project Dashboard
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    const proj = projectActionsMenu?.project;
                                    setProjectActionsMenu({ anchorEl: null, project: null });
                                    if (proj?.reference) openInformationDashboard(proj.reference);
                                }}
                            >
                                Information Portal
                            </MenuItem>
                        </Menu>
                    );
                })()}

                {snapshotOpen && (
                    <div
                        role="dialog"
                        aria-modal="true"
                        onClick={() => { setSnapshotOpen(false); setSnapshotText(''); setSnapshotError(''); setSnapshotLoading(false); setSnapshotView('structured'); setSnapshotExpandedSections({}); }}
                        style={{
                            position: 'fixed',
                            inset: 0,
                            background: 'rgba(0,0,0,0.35)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 16,
                            zIndex: 1000
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                width: 'min(1120px, 100%)',
                                maxHeight: '80vh',
                                overflow: 'auto',
                                background: 'var(--panel)',
                                borderRadius: 10,
                                border: '1px solid var(--border)',
                                boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                                color: 'var(--text)'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
                                <div style={{ fontWeight: 700 }}>Snapshot Summary — {snapshotRef}</div>
                                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                    <Button
                                        variant={snapshotView === 'structured' ? 'contained' : 'outlined'}
                                        size="small"
                                        onClick={() => setSnapshotView('structured')}
                                        disabled={!snapshotStructured.hasStructuredContent}
                                        sx={snapshotView === 'structured' ? { bgcolor: '#0f172a', '&:hover': { bgcolor: '#111827' } } : undefined}
                                    >
                                        Structured
                                    </Button>
                                    <Button
                                        variant={snapshotView === 'raw' ? 'contained' : 'outlined'}
                                        size="small"
                                        onClick={() => setSnapshotView('raw')}
                                        sx={snapshotView === 'raw' ? { bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } } : undefined}
                                    >
                                        Raw Output
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => { setSnapshotOpen(false); setSnapshotText(''); setSnapshotError(''); setSnapshotLoading(false); setSnapshotView('structured'); setSnapshotExpandedSections({}); }}
                                    >
                                        Close
                                    </Button>
                                </Stack>
                            </div>
                            <div style={{ padding: 14 }}>
                                {snapshotLoading && <div style={{ color: 'var(--muted)' }}>Generating Summary...</div>}
                                {snapshotError && (
                                    <div style={{ marginTop: 8, padding: 10, border: '1px solid #f3c2c2', background: '#fff5f5', borderRadius: 6, color: '#8a1f1f' }}>
                                        {snapshotError}
                                    </div>
                                )}
                                {!snapshotLoading && !snapshotError && snapshotView === 'structured' && snapshotStructured.hasStructuredContent && (
                                    <Stack spacing={2.5}>
                                        {!!snapshotStructured.takeaways.length && (
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 3,
                                                    background: `linear-gradient(135deg, ${alpha('#0f172a', 0.96)} 0%, ${alpha('#1e293b', 0.92)} 100%)`,
                                                    color: '#fff',
                                                    border: `1px solid ${alpha('#0f172a', 0.22)}`
                                                }}
                                            >
                                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { md: 'flex-start' } }}>
                                                    <Box sx={{ minWidth: { md: 180 } }}>
                                                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', color: alpha('#ffffff', 0.7), mb: 0.75 }}>
                                                            Quick Read
                                                        </Typography>
                                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                            AI summary at a glance
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'grid', gap: 1.1, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, flex: 1 }}>
                                                        {snapshotStructured.takeaways.map((item) => (
                                                            <Box
                                                                key={item.key}
                                                                sx={{
                                                                    p: 1.25,
                                                                    borderRadius: 2.5,
                                                                    backgroundColor: alpha('#ffffff', 0.08),
                                                                    border: `1px solid ${alpha('#ffffff', 0.12)}`
                                                                }}
                                                            >
                                                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: alpha('#ffffff', 0.62), mb: 0.5 }}>
                                                                    {item.label}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.5 }}>
                                                                    {item.text}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Stack>
                                            </Box>
                                        )}

                                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' } }}>
                                            {snapshotStructured.sections.map((section) => {
                                                const lines = getSnapshotDisplayLines(section.value);
                                                const isExpanded = Boolean(snapshotExpandedSections?.[section.key]);
                                                const canExpand = hasLongSnapshotSection(section.value);
                                                const visibleLines = canExpand && !isExpanded ? lines.slice(0, 4) : lines;

                                                return (
                                                    <Box
                                                        key={section.key}
                                                        sx={{
                                                            p: 2,
                                                            borderRadius: 3,
                                                            border: `1px solid ${alpha('#0f172a', 0.08)}`,
                                                            backgroundColor: alpha('#ffffff', 0.86),
                                                            boxShadow: `0 18px 36px ${alpha('#0f172a', 0.06)}`
                                                        }}
                                                    >
                                                        <Stack spacing={1.25}>
                                                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                                    {section.title}
                                                                </Typography>
                                                                {section.key === 'actions' && (
                                                                    <Chip label="Priority" size="small" sx={{ fontWeight: 700, backgroundColor: alpha('#f59e0b', 0.14), color: '#b45309' }} />
                                                                )}
                                                            </Stack>
                                                            <Stack spacing={0.85}>
                                                                {visibleLines.map((line, index) => {
                                                                    const isBullet = /^[-*•]\s+/.test(line);
                                                                    const text = line.replace(/^[-*•]\s+/, '').trim();
                                                                    return (
                                                                        <Box key={`${section.key}-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                                            <Box
                                                                                sx={{
                                                                                    width: 8,
                                                                                    height: 8,
                                                                                    borderRadius: '999px',
                                                                                    mt: isBullet ? 0.8 : 0.9,
                                                                                    backgroundColor: isBullet
                                                                                        ? (section.key === 'actions' ? '#f59e0b' : '#2563eb')
                                                                                        : alpha('#0f172a', 0.18),
                                                                                    flexShrink: 0
                                                                                }}
                                                                            />
                                                                            <Typography sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
                                                                                {text || line}
                                                                            </Typography>
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Stack>
                                                            {canExpand && (
                                                                <Box>
                                                                    <Button size="small" onClick={() => toggleSnapshotSection(section.key)}>
                                                                        {isExpanded ? 'Show less' : 'Show more'}
                                                                    </Button>
                                                                </Box>
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                );
                                            })}
                                        </Box>

                                        {!!snapshotStructured.risks.length && (
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 3,
                                                    border: `1px solid ${alpha('#0f172a', 0.08)}`,
                                                    backgroundColor: alpha('#f8fafc', 0.95)
                                                }}
                                            >
                                                <Stack spacing={1.5}>
                                                    <Box>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                            Risk Signals
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                            Structured scan of the RAG items returned by the AI summary.
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' } }}>
                                                        {snapshotStructured.risks.map((risk) => {
                                                            const tone = ragColor(risk.rating || '');
                                                            return (
                                                                <Box
                                                                    key={risk.label}
                                                                    sx={{
                                                                        p: 1.5,
                                                                        borderRadius: 2.5,
                                                                        backgroundColor: alpha('#ffffff', 0.94),
                                                                        border: `1px solid ${alpha(tone, 0.18)}`
                                                                    }}
                                                                >
                                                                    <Stack spacing={1}>
                                                                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                                                            <Typography sx={{ fontWeight: 700 }}>{risk.label}</Typography>
                                                                            {risk.rating ? (
                                                                                <Chip
                                                                                    size="small"
                                                                                    label={risk.rating}
                                                                                    sx={{
                                                                                        fontWeight: 700,
                                                                                        color: tone,
                                                                                        backgroundColor: alpha(tone, 0.12),
                                                                                        border: `1px solid ${alpha(tone, 0.18)}`
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <Chip
                                                                                    size="small"
                                                                                    label="Narrative"
                                                                                    sx={{ fontWeight: 700, backgroundColor: alpha('#0f172a', 0.06) }}
                                                                                />
                                                                            )}
                                                                        </Stack>
                                                                        <Typography sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                                                            {risk.narrative || risk.rawValue || 'No additional narrative returned.'}
                                                                        </Typography>
                                                                    </Stack>
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                </Stack>
                                            </Box>
                                        )}
                                    </Stack>
                                )}
                                {!snapshotLoading && !snapshotError && (snapshotView === 'raw' || !snapshotStructured.hasStructuredContent) && (
                                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit', lineHeight: 1.5 }}>
                                        {snapshotText || 'No summary returned.'}
                                    </pre>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Box>
        );
    }

    // Project summary view
    const selectedPhotoCount = Array.isArray(sitePhotoUrls) && sitePhotoUrls.length
        ? sitePhotoUrls.length
        : [selected?.imageUrl, selected?.imageUrl2, selected?.imageUrl3].filter(Boolean).length;
    return (
        <Box className={`reports-project-summary device-${deviceType}`} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {renderChangesModal()}
            {renderNamePromptDialog()}
            <AnalyticsPageHero
                eyebrow="Project Dashboard"
                title={selected?.name ? selected.name : 'Project'}
                actions={(
                    <Button variant="outlined" onClick={handleBack}>
                        Back to Projects
                    </Button>
                )}
            />

            <Box
                sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }
                }}
            >
                <AnalyticsMetricCard
                    icon={<TimelineRoundedIcon fontSize="small" />}
                    label="Timeline Steps"
                    value={(coreTimelineSteps?.length || 0) + (programmeTimelineSteps?.length || 0)}
                    caption="Combined core milestones and estimated programme stages visible on the current timeline."
                    accent="linear-gradient(135deg, #0f172a, #2563eb)"
                />
                <AnalyticsMetricCard
                    icon={<AutoAwesomeRoundedIcon fontSize="small" />}
                    label="AI Sections"
                    value={aiNarrativeRows.length}
                    caption="Structured AI narrative blocks currently shown for this project dashboard."
                    accent="linear-gradient(135deg, #7c3aed, #4338ca)"
                />
                <AnalyticsMetricCard
                    icon={<PhotoLibraryRoundedIcon fontSize="small" />}
                    label="Site Photos"
                    value={selectedPhotoCount}
                    caption="Site imagery currently available through the live carousel or fallback project photos."
                    accent="linear-gradient(135deg, #0f766e, #14b8a6)"
                />
                <AnalyticsMetricCard
                    icon={<FolderOpenRoundedIcon fontSize="small" />}
                    label="Report Versions"
                    value={reportLog.length}
                    caption="Logged report generations, QA progress, issue state, and version downloads for this project."
                    accent="linear-gradient(135deg, #b45309, #ef4444)"
                />
            </Box>

            <Stack spacing={3}>

                {/* New design (full width): Project Summary */}
                <Paper sx={{ ...analyticsPanelSx }} className="report-card reports-project-dashboard-summary">
                    <div style={{ display: 'flex', alignItems: isPhone ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: isPhone ? 'wrap' : 'nowrap' }}>
                        <h2 style={{ margin: 0, fontSize: isPhone ? 18 : undefined, lineHeight: isPhone ? 1.2 : undefined }}>Key Information</h2>
                        {!isEditing && (selected.borrower || selected.borrowerCompany || selected.borrowerEmail) && (
                            <Button
                                variant={isPhone ? 'outlined' : 'contained'}
                                color={isPhone ? 'primary' : 'warning'}
                                size="small"
                                onClick={handleEdit}
                                sx={isPhone ? { alignSelf: 'flex-start' } : undefined}
                            >
                                Edit
                            </Button>
                        )}
                    </div>

                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2.25,
                            gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.5fr) minmax(360px, 0.95fr)' },
                            alignItems: 'start'
                        }}
                    >
                        <form
                            className="reports-project-dashboard-summary-grid"
                            onSubmit={e => { e.preventDefault(); handleSave(); }}
                        >
                            {summaryFields.map(f => (
                                <div key={f.key} className="reports-project-dashboard-summary-field">
                                    <label className="reports-project-dashboard-summary-label">{f.label}</label>
                                    {f.calculated ? (
                                        <div className="reports-project-dashboard-summary-value">{(() => {
                                            const start = summaryFromInitialReport.worksStartYm;
                                            const duration = summaryFromInitialReport.durationMonths;
                                            if (!start || !/^\d{4}-\d{2}$/.test(start) || !duration || duration < 1) return 'TBC';
                                            const [year, month] = start.split('-').map(Number);
                                            const endDate = new Date(year, month - 1 + duration, 1);
                                            const monthNames = [
                                                'January', 'February', 'March', 'April', 'May', 'June',
                                                'July', 'August', 'September', 'October', 'November', 'December'
                                            ];
                                            return `${monthNames[endDate.getMonth()]}-${endDate.getFullYear()}`;
                                        })()}</div>
                                    ) : isFieldEditable(f) ? (
                                        <input
                                            type={f.key === 'borrowerEmail' ? 'email' : 'text'}
                                            value={editFields[f.key] || ''}
                                            onChange={e => handleFieldChange(f.key, e.target.value)}
                                            placeholder={`Enter ${f.label}`}
                                            className="reports-project-dashboard-summary-input"
                                        />
                                    ) : f.key === 'leadSurveyor' && selected[f.key] ? (
                                        <div className="reports-project-dashboard-summary-value">{getLeadSurveyorName(selected[f.key])}</div>
                                    ) : f.key === 'funder' && (selected.funderDetails || selected[f.key]) ? (
                                        <div className="reports-project-dashboard-summary-value">{getFunderName(selected.funderDetails || selected[f.key])}</div>
                                    ) : f.key === 'facility' ? (
                                        <div className="reports-project-dashboard-summary-value">{summaryFromInitialReport.facilityDisplay}</div>
                                    ) : f.key === 'duration' ? (
                                        <div className="reports-project-dashboard-summary-value">{summaryFromInitialReport.durationDisplay === 'TBC' ? 'TBC' : `${summaryFromInitialReport.durationDisplay} months`}</div>
                                    ) : f.key === 'worksStartDate' ? (
                                        <div className="reports-project-dashboard-summary-value">{(() => {
                                            const v = summaryFromInitialReport.worksStartYm;
                                            if (!v) return 'TBC';
                                            const [year, month] = v.split('-');
                                            const monthNames = [
                                                'January', 'February', 'March', 'April', 'May', 'June',
                                                'July', 'August', 'September', 'October', 'November', 'December'
                                            ];
                                            const mIdx = parseInt(month, 10) - 1;
                                            return `${monthNames[mIdx]}-${year}`;
                                        })()}</div>
                                    ) : selected[f.key] ? (
                                        <div className="reports-project-dashboard-summary-value">{selected[f.key]}</div>
                                    ) : (
                                        <div className="reports-project-dashboard-summary-value" style={{ color: 'var(--muted)' }}>—</div>
                                    )}
                                </div>
                            ))}

                            {(
                                (!selected.borrower || !selected.borrowerEmail) &&
                                (editFields.borrower || editFields.borrowerEmail)
                            ) && !isEditing && (
                                    <Button type="submit" variant="contained" fullWidth={isPhone} sx={{ gridColumn: '1 / -1', mt: 1.5 }}>Save</Button>
                                )}
                            {isEditing && (
                                <Button type="submit" variant="contained" fullWidth={isPhone} sx={{ gridColumn: '1 / -1', mt: 1.5 }}>Save</Button>
                            )}
                        </form>

                        <Stack spacing={1.5}>
                            <Box
                                sx={{
                                    p: 1.4,
                                    borderRadius: 3,
                                    border: `1px solid ${alpha('#0f172a', 0.08)}`,
                                    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#f8fbff', 0.88)} 100%)`
                                }}
                            >
                                <Stack spacing={1.1}>
                                    <Box>
                                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.5 }}>
                                            Location Map
                                        </Typography>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                            Project site and selected office
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            The dashboard pins the current site against the selected organisation office so routes can be compared across your configured locations.
                                        </Typography>
                                    </Box>

                                    {selectedProjectAddress ? (
                                        <Box sx={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${alpha('#0f172a', 0.08)}`, backgroundColor: alpha('#eaf3ff', 0.8) }}>
                                            <UKProjectMap
                                                projects={selectedProjectMapItems}
                                                officeSettings={selectedProjectOffice ? { ...selectedProjectOffice, brand_upload_url: projectOfficeMapSettings?.brand_upload_url || '' } : null}
                                                showDirections={Boolean(selectedProjectOfficeAddress && selectedProjectAddress)}
                                                routeMode={selectedProjectRouteMode}
                                            />
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                minHeight: 240,
                                                borderRadius: 3,
                                                border: `1px dashed ${alpha('#0f172a', 0.16)}`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                textAlign: 'center',
                                                px: 2,
                                                color: 'text.secondary',
                                                backgroundColor: alpha('#f8fafc', 0.9)
                                            }}
                                        >
                                            Add a site address to show the project pin and journey options here.
                                        </Box>
                                    )}
                                </Stack>
                            </Box>

                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 3,
                                    border: `1px solid ${alpha('#0f172a', 0.08)}`,
                                    backgroundColor: alpha('#ffffff', 0.82)
                                }}
                            >
                                <Stack spacing={1.25}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-start' } }}>
                                        <Box>
                                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.45 }}>
                                                Journey Setup
                                            </Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                Route origin office
                                            </Typography>
                                        </Box>
                                        <Chip
                                            size="small"
                                            label={projectOfficeMapLoading ? 'Loading office…' : (selectedProjectOfficeAddress ? 'Route ready' : 'Office needed')}
                                            sx={{
                                                fontWeight: 700,
                                                backgroundColor: selectedProjectOfficeAddress ? alpha('#10b981', 0.14) : alpha('#f59e0b', 0.14),
                                                color: selectedProjectOfficeAddress ? '#047857' : '#b45309'
                                            }}
                                        />
                                    </Stack>

                                    {(Array.isArray(projectOfficeMapSettings?.offices) ? projectOfficeMapSettings.offices.length : 0) > 1 ? (
                                        <TextField
                                            select
                                            size="small"
                                            label="Office"
                                            value={selectedProjectOfficeId}
                                            onChange={(e) => setSelectedProjectOfficeId(e.target.value)}
                                            sx={{ maxWidth: { xs: '100%', sm: 280 } }}
                                            helperText="Choose which configured office to use as the route origin for this project."
                                        >
                                            {(projectOfficeMapSettings.offices || []).map((office, index) => (
                                                <MenuItem key={office.id} value={office.id}>
                                                    {office.office_location || `Office ${index + 1}`}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    ) : null}

                                    <Box sx={{ p: 1.3, borderRadius: 2.5, backgroundColor: alpha('#f8fafc', 0.96), border: `1px solid ${alpha('#0f172a', 0.06)}` }}>
                                        <Typography sx={{ fontWeight: 700, mb: 0.45 }}>
                                            {selectedProjectOfficeName}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                            {selectedProjectOfficeAddress || 'Configure the organisation office address in Settings > Office Map to enable route planning.'}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ p: 1.3, borderRadius: 2.5, backgroundColor: alpha('#f8fbff', 0.96), border: `1px solid ${alpha('#2563eb', 0.08)}` }}>
                                        <Typography sx={{ fontWeight: 700, mb: 0.45 }}>
                                            Site destination
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                            {selectedProjectAddress || 'No site address recorded for this project yet.'}
                                        </Typography>
                                    </Box>

                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                        <Button
                                            variant={selectedProjectRouteMode === 'driving' ? 'contained' : 'outlined'}
                                            disabled={!selectedProjectOfficeAddress || !selectedProjectAddress}
                                            onClick={() => setSelectedProjectRouteMode('driving')}
                                            sx={{ flex: 1, justifyContent: 'center' }}
                                        >
                                            Show Driving Route
                                        </Button>
                                        <Button
                                            variant={selectedProjectRouteMode === 'transit' ? 'contained' : 'outlined'}
                                            disabled={!selectedProjectOfficeAddress || !selectedProjectAddress}
                                            onClick={() => setSelectedProjectRouteMode('transit')}
                                            sx={selectedProjectRouteMode === 'transit' ? { flex: 1, justifyContent: 'center', bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } } : { flex: 1, justifyContent: 'center' }}
                                        >
                                            Show Transit Route
                                        </Button>
                                    </Stack>

                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        The selected route is drawn directly on the map using the chosen office as the origin and the current project address as the destination.
                                    </Typography>
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>

                    {/* Portal CTA removed per dashboard design update */}
                </Paper>

                {/* New design (full width): Timeline */}
                <Paper sx={{ ...analyticsPanelSx }} className="report-card reports-project-dashboard-timeline">
                    <div style={{ fontWeight: 900, marginBottom: 10 }}>Project timeline</div>
                    <div className="reports-project-timeline">
                        {projectTimelineLoading ? (
                            <div style={{ color: 'var(--muted)', padding: '6px 0' }}>Loading timeline…</div>
                        ) : projectTimelineError ? (
                            <div style={{ color: 'var(--muted)', padding: '6px 0' }}>{projectTimelineError}</div>
                        ) : (coreTimelineSteps.length || programmeTimelineSteps.length) ? (
                            (() => {
                                const renderSteps = (steps) => steps.map((s, idx) => (
                                    <div key={s.key} className="reports-project-timeline-step">
                                        <div
                                            className={`reports-project-timeline-dot ${s.completed ? 'is-complete' : (s.isAlert ? 'is-alert' : '')}`}
                                            title={s.dateLabel || ''}
                                        >
                                            {s.completed ? '✓' : (s.isAlert ? '!' : '')}
                                        </div>
                                        <div className="reports-project-timeline-label">{s.label}</div>
                                        <div className={`reports-project-timeline-date ${s.isAlert ? 'is-alert' : ''}`}>{s.dateLabel || '—'}</div>
                                        {idx < steps.length - 1 ? (
                                            <div className={`reports-project-timeline-line ${steps[idx + 1]?.completed ? 'is-complete' : ''}`} />
                                        ) : null}
                                    </div>
                                ));

                                return (
                                    <>
                                        {/* Core milestones */}
                                        {coreTimelineSteps.length ? renderSteps(coreTimelineSteps) : null}
                                    </>
                                );
                            })()
                        ) : (
                            <div style={{ color: 'var(--muted)', padding: '6px 0' }}>No timeline data.</div>
                        )}
                    </div>

                    {(!projectTimelineLoading && !projectTimelineError && programmeTimelineSteps.length) ? (
                        <div style={{ marginTop: 10 }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 10,
                                marginBottom: 6
                            }}>
                                <div style={{ fontWeight: 900, fontSize: 13 }}>Estimated programme ({programmeTimelineSteps.length})</div>
                            </div>

                            <div className="reports-project-timeline">
                                {programmeTimelineSteps.map((s, idx) => (
                                    <div key={s.key} className="reports-project-timeline-step">
                                        <div
                                            className={`reports-project-timeline-dot ${s.completed ? 'is-complete' : (s.isAlert ? 'is-alert' : '')}`}
                                            title={s.dateLabel || ''}
                                        >
                                            {s.completed ? '✓' : (s.isAlert ? '!' : '')}
                                        </div>
                                        <div className="reports-project-timeline-label">{s.label}</div>
                                        <div className={`reports-project-timeline-date ${s.isAlert ? 'is-alert' : ''}`}>{s.dateLabel || '—'}</div>
                                        {idx < programmeTimelineSteps.length - 1 ? (
                                            <div className={`reports-project-timeline-line ${programmeTimelineSteps[idx + 1]?.completed ? 'is-complete' : ''}`} />
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </Paper>

                {/* New design (full width): AI narratives + Risk */}
                <Paper sx={{ ...analyticsPanelSx }} className="report-card reports-project-dashboard-ai">
                    {isPhone ? (
                        <div className="reports-project-ai-mobile">
                            {aiNarrativeRows.map((r) => {
                                const isOutstandingInfoRow = String(r.label || '').trim().toLowerCase() === 'outstanding info';
                                const risk = String(r.risk || '').trim();
                                const bg = ragColor(risk);
                                const riskStyle = risk
                                    ? { background: bg, color: '#fff', borderColor: bg }
                                    : { background: 'var(--panel)', color: 'var(--muted)', borderColor: 'var(--border)' };

                                const isOpen = openAiNarrativeLabel === r.label;
                                const rowId = `ai-narrative-${String(r.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

                                return (
                                    <div key={r.label} className="reports-project-ai-mobile-row">
                                        <button
                                            type="button"
                                            className={`reports-project-ai-mobile-toggle ${isOpen ? 'is-open' : ''}`}
                                            aria-expanded={isOpen}
                                            aria-controls={rowId}
                                            onClick={() => setOpenAiNarrativeLabel((prev) => (prev === r.label ? '' : r.label))}
                                        >
                                            <div className="reports-project-ai-mobile-header">
                                                <div className="reports-project-ai-mobile-label">{r.label}</div>
                                                <div className="reports-project-ai-mobile-right">
                                                    {!isOutstandingInfoRow ? (
                                                        <div
                                                            className="reports-project-ai-mobile-risk"
                                                            style={riskStyle}
                                                            title={riskAiError
                                                                ? `AI risk classification failed: ${riskAiError}`
                                                                : 'AI-classified risk level (Low/Medium/High mapped to Green/Yellow/Red).'}
                                                        >
                                                            {riskAiLoading ? '…' : (risk || '—')}
                                                        </div>
                                                    ) : null}
                                                    <div className="reports-project-ai-mobile-chevron" aria-hidden="true">
                                                        {isOpen ? '▾' : '▸'}
                                                    </div>
                                                </div>
                                            </div>
                                        </button>

                                        {isOpen ? (
                                            <div id={rowId} className="reports-project-ai-mobile-text">
                                                {r.text}
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="reports-scroll-x">
                            <table className="reports-project-ai-table" style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--panel)' }}>
                                <thead>
                                    <tr style={{ background: 'var(--input-bg)' }}>
                                        <th style={{ padding: 10, border: '1px solid var(--border)', textAlign: 'left', minWidth: 180 }}> </th>
                                        <th style={{ padding: 10, border: '1px solid var(--border)', textAlign: 'left', minWidth: 520 }}> </th>
                                        <th style={{ padding: 10, border: '1px solid var(--border)', textAlign: 'center', width: 170 }}>
                                            AI Generated Risk Level
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {aiNarrativeRows.map((r) => {
                                        const isOutstandingInfoRow = String(r.label || '').trim().toLowerCase() === 'outstanding info';
                                        const risk = String(r.risk || '').trim();
                                        const bg = ragColor(risk);
                                        const riskStyle = risk
                                            ? { background: bg, color: '#fff', borderColor: bg }
                                            : { background: 'var(--panel)', color: 'var(--muted)', borderColor: 'var(--border)' };

                                        return (
                                            <tr key={r.label}>
                                                <td style={{ padding: 10, border: '1px solid var(--border)', fontWeight: 900, width: 200, verticalAlign: 'top' }}>
                                                    {r.label}
                                                </td>
                                                <td style={{ padding: 10, border: '1px solid var(--border)', verticalAlign: 'top' }}>
                                                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.35 }}>{r.text}</div>
                                                </td>
                                                <td style={{ padding: 10, border: '1px solid var(--border)', textAlign: 'center', verticalAlign: 'top' }}>
                                                    {!isOutstandingInfoRow ? (
                                                        <div
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                minWidth: 120,
                                                                padding: '8px 10px',
                                                                borderRadius: 10,
                                                                border: `1px solid ${riskStyle.borderColor}`,
                                                                fontWeight: 900,
                                                                ...riskStyle
                                                            }}
                                                            title={riskAiError
                                                                ? `AI risk classification failed: ${riskAiError}`
                                                                : 'AI-classified risk level (Low/Medium/High mapped to Green/Yellow/Red).'}
                                                        >
                                                            {riskAiLoading ? '…' : (risk || '—')}
                                                        </div>
                                                    ) : null}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Paper>

                {/* Existing photos card (MUST remain) — moved below the new design */}
                {isPhone && photoLightboxOpen ? (
                    <div className="reports-photo-lightbox" role="dialog" aria-modal="true" onClick={closeLightbox}>
                        <div className="reports-photo-lightbox-inner" onClick={(e) => e.stopPropagation()}>
                            <button
                                type="button"
                                className="reports-photo-lightbox-close"
                                onClick={closeLightbox}
                                aria-label="Close photo"
                            >
                                ×
                            </button>

                            <button
                                type="button"
                                className="reports-photo-lightbox-nav is-left"
                                onClick={() => {
                                    if (!canCycleLightbox) return;
                                    setPhotoLightboxIndex((i) => {
                                        const n = phonePhotoUrls.length || 1;
                                        return (i - 1 + n) % n;
                                    });
                                }}
                                disabled={!canCycleLightbox}
                                aria-label="Previous photo"
                            >
                                ‹
                            </button>

                            <img
                                className="reports-photo-lightbox-img"
                                src={phonePhotoUrls[photoLightboxIndex] || svgPlaceholderDataUri('Project Photo')}
                                alt=""
                            />

                            <button
                                type="button"
                                className="reports-photo-lightbox-nav is-right"
                                onClick={() => {
                                    if (!canCycleLightbox) return;
                                    setPhotoLightboxIndex((i) => {
                                        const n = phonePhotoUrls.length || 1;
                                        return (i + 1) % n;
                                    });
                                }}
                                disabled={!canCycleLightbox}
                                aria-label="Next photo"
                            >
                                ›
                            </button>

                            <div className="reports-photo-lightbox-meta">
                                {Math.min(photoLightboxIndex + 1, phonePhotoUrls.length)} / {phonePhotoUrls.length}
                            </div>
                        </div>
                    </div>
                ) : null}

                <Paper sx={{ ...analyticsPanelSx }} className="report-card reports-project-photos">
                    <div className="reports-project-photos-inner">
                        {Array.isArray(sitePhotoUrls) && sitePhotoUrls.length ? (
                            <div className="reports-project-photo-carousel">
                                <div className="reports-project-photo-carousel-main">
                                    <button
                                        type="button"
                                        className="reports-project-photo-carousel-btn"
                                        onClick={goPrevSitePhoto}
                                        disabled={!canCycleSitePhotos || sitePhotoLoading}
                                        aria-label="Previous photo"
                                    >
                                        ‹
                                    </button>

                                    <img
                                        key={activeSitePhotoUrl || 'site-photo'}
                                        className={`reports-project-photo reports-project-photo-carousel-image ${sitePhotoLoading ? 'is-loading' : ''}`}
                                        src={activeSitePhotoUrl || svgPlaceholderDataUri('Project Photo')}
                                        alt={`Site ${Math.min(sitePhotoIndex + 1, sitePhotoUrls.length)} of ${sitePhotoUrls.length}`}
                                        onLoad={() => setSitePhotoLoading(false)}
                                        onError={() => setSitePhotoLoading(false)}
                                        onClick={() => openLightboxAt(sitePhotoIndex)}
                                        style={isPhone ? { cursor: 'zoom-in' } : undefined}
                                    />

                                    <button
                                        type="button"
                                        className="reports-project-photo-carousel-btn"
                                        onClick={goNextSitePhoto}
                                        disabled={!canCycleSitePhotos || sitePhotoLoading}
                                        aria-label="Next photo"
                                    >
                                        ›
                                    </button>
                                </div>

                                <div className="reports-project-photo-carousel-meta">
                                    {Math.min(sitePhotoIndex + 1, sitePhotoUrls.length)} / {sitePhotoUrls.length}
                                    {(() => {
                                        const takenAt = activeSitePhoto?.taken_at ? new Date(activeSitePhoto.taken_at) : null;
                                        const hasTaken = takenAt && Number.isFinite(takenAt.getTime());
                                        const raw = activeSitePhoto?.taken_at_raw ? String(activeSitePhoto.taken_at_raw) : '';

                                        if (hasTaken) {
                                            return (
                                                <span style={{ marginLeft: 10, color: '#64748b' }}>
                                                    Taken: {takenAt.toLocaleString()}
                                                </span>
                                            );
                                        }

                                        if (raw) {
                                            return (
                                                <span style={{ marginLeft: 10, color: '#64748b' }}>
                                                    Taken: {raw}
                                                </span>
                                            );
                                        }

                                        if (activeSitePhoto?.created_at) {
                                            return (
                                                <span style={{ marginLeft: 10, color: '#64748b' }}>
                                                    Uploaded: {new Date(activeSitePhoto.created_at).toLocaleString()}
                                                </span>
                                            );
                                        }

                                        return null;
                                    })()}
                                </div>

                                <div className="reports-project-photo-carousel-thumbs">
                                    {sitePhotoUrls.slice(0, 12).map((u, idx) => {
                                        const isActive = idx === sitePhotoIndex;
                                        return (
                                            <button
                                                key={`${u}|${idx}`}
                                                type="button"
                                                className={`reports-project-photo-thumb ${isActive ? 'is-active' : ''}`}
                                                onClick={() => {
                                                    if (idx === sitePhotoIndex) return;
                                                    setSitePhotoLoading(true);
                                                    setSitePhotoIndex(idx);
                                                }}
                                                disabled={sitePhotoLoading}
                                                aria-label={`Select photo ${idx + 1}`}
                                            >
                                                <img src={u} alt="" className="reports-project-photo-thumb-img" />
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
                                <img
                                    className="reports-project-photo"
                                    src={selected.imageUrl || svgPlaceholderDataUri('Project Photo')}
                                    alt=""
                                    onClick={() => openLightboxAt(0)}
                                    style={isPhone ? { cursor: 'zoom-in' } : undefined}
                                />
                                <img
                                    className="reports-project-photo"
                                    src={selected.imageUrl2 || svgPlaceholderDataUri('Photo 2')}
                                    alt=""
                                    onClick={() => openLightboxAt(1)}
                                    style={isPhone ? { cursor: 'zoom-in' } : undefined}
                                />
                                <img
                                    className="reports-project-photo"
                                    src={selected.imageUrl3 || svgPlaceholderDataUri('Photo 3')}
                                    alt=""
                                    onClick={() => openLightboxAt(2)}
                                    style={isPhone ? { cursor: 'zoom-in' } : undefined}
                                />
                            </div>
                        )}
                    </div>
                </Paper>

                {/* Legacy summary card removed in favor of the new design */}

                {/* Dashboard cards for Planning Conditions / Costs / Risk Matrix removed (shown in Information Portal) */}

                {/* Full width: Drawdown chart/table */}
                <Paper sx={{ ...analyticsPanelSx }} className="report-card reports-project-scurve-card">
                    <h3 style={{ marginBottom: 20 }}>Project Drawdown History</h3>
                    <SCurveChart
                        baselineAmount={sCurveBaseline?.amount}
                        baselineSource={sCurveBaseline?.source}
                        duration={summaryFromInitialReport?.durationMonths || selected.duration}
                        storageKeyPrefix={selected.reference}
                        prefillActuals={drawdownPrefillActuals}
                    />
                </Paper>

                {/* Row 5 (full width): Report Log */}
                <Paper sx={{ ...analyticsPanelSx }} className="report-card reports-project-log-card">
                    <h3 style={{ marginBottom: 16 }}>Report Log</h3>
                    {reportLogSource === 'local' && (
                        <div style={{ marginBottom: 10, color: 'var(--muted)', fontSize: 12 }}>
                            Using legacy local report log (backend not available).
                        </div>
                    )}
                    <div className="reports-scroll-x">
                        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--panel)' }}>
                            <thead>
                                <tr style={{ background: 'var(--input-bg)' }}>
                                    <th style={{ padding: '10px', border: '1px solid var(--border)' }}>Report</th>
                                    <th style={{ padding: '10px', border: '1px solid var(--border)' }}>{getReportVersionNoun(reportVersionNaming)}</th>
                                    <th style={{ padding: '10px', border: '1px solid var(--border)' }}>Date of Generation</th>
                                    <th style={{ padding: '10px', border: '1px solid var(--border)' }}>Status</th>
                                    <th style={{ padding: '10px', border: '1px solid var(--border)' }}>QA</th>
                                    <th style={{ padding: '10px', border: '1px solid var(--border)' }}>QA Name</th>
                                    <th style={{ padding: '10px', border: '1px solid var(--border)' }}>Issue</th>
                                    <th style={{ padding: '10px', border: '1px solid var(--border)' }}>Issued By</th>
                                    <th style={{ padding: '10px', border: '1px solid var(--border)' }}>Issue Date</th>
                                    <th style={{ padding: '10px', border: '1px solid var(--border)' }}>Downloads</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportLog.map((row, idx) => {
                                    const status = row.issueChecked ? 'Issued' : (row.qaChecked ? 'QA' : 'Draft');
                                    const rowId = row?.id != null ? Number(row.id) : null;
                                    const highlight = rowId != null && deepLinkRef?.reportLogId != null && Number(deepLinkRef.reportLogId) === rowId;
                                    return (
                                        <tr
                                            key={row.id != null ? row.id : idx}
                                            id={rowId != null ? `report-log-row-${rowId}` : undefined}
                                            style={highlight ? { background: 'rgba(251, 191, 36, 0.16)' } : undefined}
                                        >
                                            <td style={{ padding: '10px', border: '1px solid var(--border)', textAlign: 'center' }}>{row.type}</td>
                                            <td style={{ padding: '10px', border: '1px solid var(--border)', textAlign: 'center' }}>{formatReportVersion(row.revision, reportVersionNaming) || row.revision}</td>
                                            <td style={{ padding: '10px', border: '1px solid var(--border)', textAlign: 'center' }}>{row.date}</td>
                                            <td style={{ padding: '10px', border: '1px solid var(--border)', textAlign: 'center', fontWeight: 700 }}>{status}</td>
                                            <td style={{ padding: '10px', border: '1px solid var(--border)', textAlign: 'center' }}>
                                                <input type="checkbox" checked={row.qaChecked} onChange={() => handleQAChange(idx)} disabled={row.qaChecked} />
                                            </td>
                                            <td style={{ padding: '10px', border: '1px solid var(--border)', textAlign: 'center' }}>{row.qaName}</td>
                                            <td style={{ padding: '10px', border: '1px solid var(--border)', textAlign: 'center' }}>
                                                <input type="checkbox" checked={row.issueChecked} onChange={() => handleIssueChange(idx)} disabled={row.issueChecked} />
                                            </td>
                                            <td style={{ padding: '10px', border: '1px solid var(--border)', textAlign: 'center' }}>{row.issueName}</td>
                                            <td style={{ padding: '10px', border: '1px solid var(--border)', textAlign: 'center' }}>{row.issueDate}</td>
                                            <td style={{ padding: '10px', border: '1px solid var(--border)', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                                                    {row.downloadHref ? (
                                                        <a
                                                            href={row.downloadHref}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            style={{ textDecoration: 'none', color: '#2563eb', fontWeight: 700 }}
                                                        >
                                                            DOCX
                                                        </a>
                                                    ) : (
                                                        <span style={{ color: 'var(--muted)' }}>DOCX—</span>
                                                    )}

                                                    {row.issuePdfHref ? (
                                                        <a
                                                            href={row.issuePdfHref}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            style={{ textDecoration: 'none', color: '#7c3aed', fontWeight: 700 }}
                                                        >
                                                            PDF
                                                        </a>
                                                    ) : (
                                                        <span style={{ color: 'var(--muted)' }}>PDF—</span>
                                                    )}

                                                    {reportLogSource === 'db' && row.id != null ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleViewChanges(idx)}
                                                            style={{
                                                                border: '1px solid var(--border)',
                                                                background: 'var(--input-bg)',
                                                                color: 'var(--text)',
                                                                padding: '4px 10px',
                                                                borderRadius: 999,
                                                                cursor: 'pointer',
                                                                fontWeight: 700
                                                            }}
                                                            title="Show changes since previous revision"
                                                        >
                                                            Changes
                                                        </button>
                                                    ) : null}

                                                    {isAdmin && reportLogSource === 'db' && row.id != null ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteReportLogRow(idx)}
                                                            disabled={deletingReportLogId != null && Number(deletingReportLogId) === Number(row.id)}
                                                            style={{
                                                                border: '1px solid rgba(220, 38, 38, 0.35)',
                                                                background: 'rgba(220, 38, 38, 0.08)',
                                                                color: '#dc2626',
                                                                padding: '4px 10px',
                                                                borderRadius: 999,
                                                                cursor: (deletingReportLogId != null && Number(deletingReportLogId) === Number(row.id)) ? 'not-allowed' : 'pointer',
                                                                fontWeight: 800
                                                            }}
                                                            title="Admin only: delete this report version and stored artifacts"
                                                        >
                                                            {deletingReportLogId != null && Number(deletingReportLogId) === Number(row.id) ? 'Deleting…' : 'Delete'}
                                                        </button>
                                                    ) : null}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Paper>
            </Stack>
        </Box>
    );
}
